(() => {
  // browser/page-lifecycle.mjs
  function pageLifecycle(fetcher = (...args) => globalThis.fetch(...args)) {
    let active = true;
    return {
      get active() {
        return active;
      },
      hide() {
        active = false;
      },
      show() {
        active = true;
      },
      fetch(...args) {
        if (!active) return Promise.reject(Object.assign(new Error("page_inactive"), { code: "page_inactive" }));
        return fetcher(...args);
      }
    };
  }

  // browser/feedback.mjs
  function noticeText(notice) {
    return { superseded: "changed", replaced_intent: "updated", invalid: "not_saved", later: "not_saved", relink_unresolved: "retry" }[notice.kind] || null;
  }
  function noticeKey(notice) {
    return JSON.stringify(notice);
  }
  function visibleNotices(state2) {
    const seen = /* @__PURE__ */ new Set();
    return [...state2.notices || []].reverse().filter((n) => {
      const key2 = noticeKey(n);
      if (!noticeText(n) || seen.has(key2)) return false;
      seen.add(key2);
      return true;
    });
  }
  function undoState(state2, undo2) {
    if (!undo2 || !state2 || undo2.inc !== state2.inc) return null;
    if (state2.outbox[undo2.seq]) return "saving";
    const result = state2.known[undo2.seq];
    const witness = result?.w;
    return ["R", "n"].includes(result?.code) && witness?.[0] === "r" && !state2.inst.items[undo2.p] && state2.inst.gone[undo2.p]?.rm === witness[1] ? "removed" : null;
  }

  // browser/marketing-sdk.mjs
  var MarketingSDK = class {
    constructor({ getSDK = () => window.klaviyo, timeout = 8e3, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (id) => clearTimeout(id), observe = () => {
    } } = {}) {
      Object.assign(this, { getSDK, timeout, setTimer, clearTimer, observe });
      this.stopped = false;
      this.pending = false;
    }
    call(method, ...args) {
      if (this.stopped) return Promise.reject(Error("marketing_stopped"));
      if (this.pending) return Promise.reject(Error("marketing_busy"));
      const sdk = this.getSDK();
      if (typeof sdk?.[method] !== "function") return Promise.reject(Error("marketing_unavailable"));
      this.pending = true;
      this.observe(method, "pending");
      return new Promise((resolve, reject) => {
        let done = false;
        const finish = (ok, value) => {
          if (done) return;
          done = true;
          this.clearTimer(timer2);
          this.pending = false;
          if (!ok) this.stopped = true;
          this.observe(method, ok ? "completed" : "failed");
          ok ? resolve(value) : reject(Error("marketing_failed"));
        };
        const timer2 = this.setTimer(() => {
          if (done) return;
          done = true;
          this.pending = false;
          this.stopped = true;
          this.observe(method, "timed_out");
          reject(Error("marketing_timeout"));
        }, this.timeout);
        try {
          const answer = sdk[method](...args, (value) => finish(true, value));
          if (answer && typeof answer.then === "function") answer.then((value) => finish(true, value), () => finish(false));
        } catch {
          finish(false);
        }
      });
    }
  };
  var marketingSDK = new MarketingSDK();

  // browser/branch-b.mjs
  var digest = async (x) => [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(x))))].map((v) => v.toString(16).padStart(2, "0")).join("");
  var fingerprint = (s) => JSON.stringify([s.scope, s.inc, s.inst.rev, Object.keys(s.inst.items).sort()]);
  async function prepareAccountPayload({ controller: controller2, identity, market, loadFresh, now = () => (/* @__PURE__ */ new Date()).toISOString() }) {
    const s = await controller2.inspect();
    if (!s || controller2.blocked || controller2.phase !== "account" || Object.keys(s.outbox).length || s.rows.length) return null;
    const before = fingerprint(s), who = await identity();
    if (!who || String(who.customer) !== String(controller2.scope.cid) || !who.key) return null;
    const newest = Object.entries(s.inst.items).sort((a, b) => b[1].g - a[1].g || Number(b[0]) - Number(a[0])), items = [];
    for (let i = 0; i < newest.length && items.length < 12; i += 24) {
      const requested = newest.slice(i, i + 24).map(([id]) => id), loaded = await loadFresh(requested), byId = new Map(loaded.filter((x) => x && requested.includes(String(x.id))).map((x) => [String(x.id), x]));
      for (const id of requested) {
        const x = byId.get(id);
        if (x?.available === true && Number.isSafeInteger(x.id) && typeof x.price === "number" && Number.isFinite(x.price) && x.currency === market.currency) {
          items.push({ ...x, nextUp: !!s.inst.items[x.id]?.f });
          if (items.length === 12) break;
        }
      }
    }
    const final = await controller2.inspect(), again = await identity();
    if (!final || controller2.blocked || Object.keys(final.outbox).length || final.rows.length || fingerprint(final) !== before || again?.key !== who.key || String(again?.customer) !== String(controller2.scope.cid)) return null;
    const common = items.map((m) => ({ ProductName: m.title, ProductID: m.id, Handle: m.handle, ImageURL: m.image, URL: m.url, Price: m.price, CompareAtPrice: m.compareAt || 0, Categories: m.type ? [m.type] : [] }));
    const payload = { WishlistCount: Object.keys(s.inst.items).length, WishlistItems: common, WishlistItemsV2: items.map((m) => ({ id: m.id, handle: m.handle, title: m.title, url: m.url, image: m.image, price: { amount: m.price, currency: m.currency, display: m.display }, compareAt: m.compareAt || 0, nextUp: m.nextUp })), WishlistSchema: 2, WishlistCustomer: controller2.scope.cid, WishlistInc: s.inc, WishlistRev: s.inst.rev, WishlistHash: await digest([before, market, items]), WishlistMarket: market, WishlistUpdatedAt: now(), WishlistSource: "account" };
    return { payload, stamp: before, identity: who.key, marker: payload.WishlistHash };
  }
  async function enqueuePrepared({ prepared, controller: controller2, identity, sink }) {
    if (!prepared || typeof sink !== "function") return false;
    const s = await controller2.inspect(), who = await identity();
    if (!s || controller2.blocked || Object.keys(s.outbox).length || s.rows.length || fingerprint(s) !== prepared.stamp || who?.key !== prepared.identity || String(who?.customer) !== String(controller2.scope.cid)) return false;
    await sink(prepared.payload);
    return true;
  }
  var BranchBSync = class {
    constructor({ controller: controller2, identity, market, loadFresh, sink, afterPrepared, enabled = false }) {
      Object.assign(this, { controller: controller2, identity, market, loadFresh, sink, afterPrepared, enabled });
      this.marker = null;
      this.identityKey = null;
      this.busy = false;
      this.generation = 0;
    }
    async run() {
      if (!this.enabled || this.busy) return false;
      this.busy = true;
      const generation = this.generation;
      try {
        const who = await this.identity();
        if (who?.key !== this.identityKey) {
          this.marker = null;
          this.identityKey = who?.key;
        }
        const p = await prepareAccountPayload(this);
        if (!p) return false;
        let sent = false;
        if (p.marker !== this.marker) {
          sent = await enqueuePrepared({ ...this, prepared: p });
          if (!sent || generation !== this.generation) return false;
          const current = await this.controller.inspect(), identity = await this.identity();
          if (!current || this.controller.blocked || Object.keys(current.outbox).length || current.rows.length || fingerprint(current) !== p.stamp || identity?.key !== p.identity) return false;
          this.marker = p.marker;
        }
        await this.afterPrepared?.(p);
        return sent;
      } catch {
        return false;
      } finally {
        this.busy = false;
      }
    }
    reset() {
      this.generation++;
      this.marker = null;
      this.identityKey = null;
    }
  };
  async function cookieIdentity(cookie) {
    const m = String(cookie).match(/(?:^|;\s*)__kla_id=([^;]+)/);
    if (!m) return null;
    try {
      const raw = JSON.parse(atob(decodeURIComponent(m[1])));
      const key2 = raw.$exchange_id || raw.$email || raw.email;
      return typeof key2 === "string" && key2 ? digest(key2) : null;
    } catch {
      return null;
    }
  }
  function guestMaySend(storage) {
    try {
      return storage.getItem("pk_wl2:kl:bound") !== "1";
    } catch {
      return false;
    }
  }
  async function prepareGuestPayload({ read, identity, market, loadFresh, bound }) {
    if (bound()) return null;
    const before = await read(), who = await identity();
    if (!who) return null;
    const sorted = Object.entries(before.guest).sort((a, b) => b[1].g - a[1].g).map(([id]) => id), items = [];
    for (let i = 0; i < sorted.length && items.length < 12; i += 24) {
      const fresh = await loadFresh(sorted.slice(i, i + 24));
      for (const x of fresh) if (x?.available && x.currency === market.currency && Number.isSafeInteger(x.id) && x.id > 0 && Number.isFinite(x.price) && !items.some((y) => y.id === x.id)) {
        items.push(x);
        if (items.length === 12) break;
      }
    }
    const after = await read();
    if (bound() || before.rev !== after.rev || JSON.stringify(before.guest) !== JSON.stringify(after.guest) || who !== await identity()) return null;
    const marker = await digest([2, before.rev, market, items]);
    return { identity: who, rev: before.rev, marker, payload: { WishlistCount: sorted.length, WishlistItems: items.map((x) => ({ ProductID: x.id, Handle: x.handle, ProductName: x.title, ImageURL: x.image, URL: x.url, Price: x.price, CompareAtPrice: x.compareAt || 0, Categories: x.type ? [x.type] : [] })), WishlistItemsV2: items.map((x) => ({ id: x.id, handle: x.handle, title: x.title, url: x.url, image: x.image, price: { amount: x.price, currency: x.currency, display: x.display }, compareAt: x.compareAt || 0, nextUp: false })), WishlistSchema: 2, WishlistInc: 0, WishlistRev: before.rev, WishlistHash: marker, WishlistMarket: market, WishlistUpdatedAt: (/* @__PURE__ */ new Date()).toISOString(), WishlistSource: "guest" } };
  }

  // browser/save-events.mjs
  var ConfirmedSaveEvents = class {
    constructor({ controller: controller2, identity, sink, enabled = false, uuid = () => crypto.randomUUID() }) {
      Object.assign(this, { controller: controller2, identity, sink, enabled, uuid });
      this.pending = /* @__PURE__ */ new Map();
      this.busy = false;
    }
    capture(p, seq, s) {
      if (!this.enabled || !s || s.stale || !Number.isSafeInteger(p) || p <= 0 || !Number.isSafeInteger(seq) || seq <= 0) return false;
      const e = s.outbox[seq];
      if (!e || e.op.t !== "save" || e.op.p !== p || e.op.imp !== void 0 || e.op.r !== void 0 || e.op.dep !== void 0) return false;
      this.pending.set(p, { p, seq, scope: s.scope, identity: s.identity, inc: s.inc, client: s.client, regN: s.regN, id: "wl2-" + this.uuid() });
      return true;
    }
    cancel(p) {
      this.pending.delete(p);
    }
    reset() {
      this.pending.clear();
    }
    async run(prepared) {
      if (!this.enabled || this.busy || !prepared || !this.pending.size) return false;
      this.busy = true;
      let queued = false;
      try {
        for (const [p, t] of [...this.pending]) {
          const s = await this.controller.inspect();
          if (!s || s.stale || s.scope !== t.scope || s.identity !== t.identity || s.inc !== t.inc || s.client !== t.client || s.regN !== t.regN) {
            this.pending.delete(p);
            continue;
          }
          if (Object.keys(s.outbox).length || s.rows.length || this.controller.blocked) continue;
          const r = s.known[t.seq], item = s.inst.items[p];
          if (!r || !["S", "n"].includes(r.code) || r.w?.[0] !== "g" || r.w[1] !== item?.g) {
            this.pending.delete(p);
            continue;
          }
          const meta = prepared.payload.WishlistItems.find((x) => x.ProductID === p);
          if (!meta) {
            this.pending.delete(p);
            continue;
          }
          const ok = await enqueuePrepared({ prepared, controller: this.controller, identity: this.identity, sink: async (payload) => {
            const end = await this.controller.inspect();
            if (this.pending.get(p) !== t || !end || end.scope !== t.scope || end.identity !== t.identity || end.inc !== t.inc || end.client !== t.client || end.regN !== t.regN || Object.keys(end.outbox).length || end.rows.length || end.known[t.seq]?.w?.[1] !== r.w[1] || end.inst.items[p]?.g !== r.w[1]) return;
            this.pending.delete(p);
            await this.sink("Added to Wishlist", { $event_id: t.id, ...meta, WishlistCount: payload.WishlistCount, WishlistSource: "account", WishlistSchema: 2 });
            queued = true;
          } });
          if (!ok) continue;
        }
      } catch {
      } finally {
        this.busy = false;
      }
      return queued;
    }
  };

  // local/src/codec.mjs
  var clone = structuredClone;

  // local/src/rules.mjs
  var MAX = 999999999999;
  var clone2 = (x) => structuredClone(x);
  var canonical = (x) => JSON.stringify(x, (_, v) => v && typeof v === "object" && !Array.isArray(v) ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]])) : v);
  var key = (x) => String(x);
  function resolveTarget(st, p) {
    const a = st.gone[p];
    return typeof p === "string" && p.startsWith("h:") && !st.items[p] && a?.ag > 0 && st.items[a.to]?.g === a.ag ? a.to : p;
  }
  function targetState(st, p) {
    const t = resolveTarget(st, p);
    return { target: t, present: !!st.items[t], item: clone2(st.items[t] ?? null), gone: clone2(st.gone[p] ?? null) };
  }
  function sameDesign(st, a, b) {
    return key(a) === key(b) || key(resolveTarget(st, a)) === key(resolveTarget(st, b));
  }
  function chainIntent(ops, initial2 = { presence: null, nextUp: null }) {
    const v = { ...initial2 };
    for (const o of ops) {
      if (o.t === "save") {
        if (v.presence === false) v.nextUp = 0;
        v.presence = true;
      } else if (o.t === "remove") {
        v.presence = false;
        v.nextUp = null;
      } else if (o.t === "next" && v.presence !== false) v.nextUp = +!!o.on;
    }
    return v;
  }
  function satisfied(intent, st, p) {
    const t = targetState(st, p);
    return (intent.presence === null || intent.presence === t.present) && (intent.nextUp === null || !t.present || intent.nextUp === t.item.f);
  }

  // local/src/storage.mjs
  var stores = ["clients", "state", "outbox", "results", "attention", "recovery", "notices"];
  function encode(root) {
    const m = Object.fromEntries(stores.map((n) => [n, /* @__PURE__ */ new Map()]));
    m.clients.set(JSON.stringify([]), root.active);
    for (const [cid, s] of Object.entries(root.streams)) {
      const { inst, outbox, known, rows, jobs, notices, ...client } = s;
      m.clients.set(JSON.stringify([cid]), client);
      m.state.set(JSON.stringify([cid]), inst);
      for (const [n, e] of Object.entries(outbox)) m.outbox.set(JSON.stringify([cid, s.inc, s.epoch, n]), e);
      for (const [n, e] of Object.entries(known)) m.results.set(JSON.stringify([cid, s.inc, s.epoch, n]), e);
      for (const r of rows) m.attention.set(JSON.stringify([cid, r.id]), r);
      m.recovery.set(JSON.stringify([cid]), jobs);
      m.notices.set(JSON.stringify([cid]), notices);
    }
    return m;
  }
  function decode(m) {
    const root = { active: { scope: null, generation: 0 }, streams: {} };
    for (const [k, v] of m.clients) {
      const id = JSON.parse(k);
      if (id.length === 0) {
        root.active = v;
        continue;
      }
      root.streams[id[0]] = { ...v, inst: null, outbox: {}, known: {}, rows: [], jobs: [], notices: [] };
    }
    for (const n of stores.slice(1)) for (const [k, v] of m[n]) {
      const [cid, , , seq] = JSON.parse(k), s = root.streams[cid];
      if (!s) continue;
      if (n === "state") s.inst = v;
      else if (n === "outbox") s.outbox[seq] = v;
      else if (n === "results") s.known[seq] = v;
      else if (n === "attention") s.rows.push(v);
      else if (n === "recovery") s.jobs = v;
      else if (n === "notices") s.notices = v;
    }
    return root;
  }
  var IDBStore = class _IDBStore {
    constructor(db, { channelFactory = (name) => typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(name), reopen = null } = {}) {
      this.db = db;
      this.reopen = reopen;
      this.closed = false;
      this.volatile = false;
      this.failNext = false;
      this.lastWrites = 0;
      this.channel = channelFactory("pk-wl-local-v10:" + db.name);
      this.channel?.unref?.();
      this.listeners = /* @__PURE__ */ new Set();
      if (this.channel) this.channel.onmessage = (e) => {
        for (const f of this.listeners) f(e.data);
      };
    }
    static open(name, indexedDBImpl = globalThis.indexedDB, options = {}) {
      return new Promise((resolve, reject) => {
        const q = indexedDBImpl.open(name, 1);
        q.onupgradeneeded = () => {
          for (const n of stores) q.result.createObjectStore(n);
        };
        q.onerror = () => reject(q.error);
        q.onsuccess = () => resolve(new _IDBStore(q.result, { ...options, reopen: () => _IDBStore.open(name, indexedDBImpl, options) }));
      });
    }
    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    }
    async reconnect(failed) {
      if (this.closed || !this.reopen) throw new DOMException("Storage intentionally closed", "InvalidStateError");
      if (this.db !== failed) return;
      if (!this.reopening) {
        this.reopening = (async () => {
          let expired = false, timer2;
          const opened = Promise.resolve().then(() => this.reopen());
          const wait = opened.then((store) => {
            if (expired || this.closed) {
              store.close();
              throw new DOMException("Reopen abandoned", "InvalidStateError");
            }
            return store;
          });
          try {
            const store = await Promise.race([wait, new Promise((_, reject) => {
              timer2 = setTimeout(() => {
                expired = true;
                reject(new DOMException("Reopen timed out", "InvalidStateError"));
              }, 5e3);
            })]);
            if (this.closed) {
              store.close();
              throw new DOMException("Storage intentionally closed", "InvalidStateError");
            }
            this.db = store.db;
            store.channel?.close();
            failed.close();
          } finally {
            clearTimeout(timer2);
          }
        })().finally(() => {
          this.reopening = null;
        });
      }
      return this.reopening;
    }
    transact(fn, { abort = false, reopened = false } = {}) {
      return new Promise((resolve, reject) => {
        let tx;
        const connection = this.db;
        try {
          tx = connection.transaction(stores, "readwrite", { durability: "strict" });
        } catch (e) {
          if (e.name === "InvalidStateError" && !reopened && !this.closed && this.reopen) {
            this.reconnect(connection).then(() => this.transact(fn, { abort, reopened: true })).then(resolve, reject);
            return;
          }
          reject(e);
          return;
        }
        if (tx.durability !== "strict") {
          tx.abort();
          reject(new DOMException("Strict durability unavailable", "InvalidStateError"));
          return;
        }
        this.lastDurability = tx.durability;
        let value, thrown, root, writes = 0;
        const before = Object.fromEntries(stores.map((n) => [n, /* @__PURE__ */ new Map()]));
        let left = stores.length;
        tx.oncomplete = () => {
          this.lastWrites = writes;
          if (writes) this.channel?.postMessage({ scope: root.active.scope, identity: root.active.generation, rev: root.streams[root.active.scope]?.inst.rev ?? null });
          resolve(clone2(value));
        };
        tx.onabort = () => reject(thrown ?? tx.error ?? new DOMException("Aborted", "AbortError"));
        tx.onerror = () => {
        };
        for (const n of stores) {
          const st = tx.objectStore(n), keys = st.getAllKeys(), values = st.getAll();
          values.onsuccess = () => {
            before[n] = new Map(keys.result.map((k, i) => [k, values.result[i]]));
            if (--left) return;
            try {
              root = decode(clone2(before));
              value = fn(root);
              if (value?.then) throw Error("async_reducer_forbidden");
              const after = encode(root);
              for (const name of stores) {
                const os = tx.objectStore(name);
                for (const k of before[name].keys()) if (!after[name].has(k)) {
                  os.delete(k);
                  writes++;
                }
                for (const [k, v] of after[name]) if (!before[name].has(k) || canonical(v) !== canonical(before[name].get(k))) {
                  os.put(v, k);
                  writes++;
                }
              }
              if (abort || this.failNext) {
                this.failNext = false;
                tx.abort();
              }
            } catch (e) {
              thrown = e;
              tx.abort();
            }
          };
        }
      });
    }
    close() {
      this.closed = true;
      this.channel?.close();
      this.db.close();
    }
  };
  var MemoryStore = class {
    constructor() {
      this.root = { active: { scope: null, generation: 0 }, streams: {} };
      this.volatile = true;
      this.failNext = false;
    }
    async transact(fn, { abort = false } = {}) {
      const root = clone2(this.root);
      const value = fn(root);
      if (value?.then) throw Error("async_reducer_forbidden");
      if (abort || this.failNext) {
        this.failNext = false;
        throw new DOMException("Aborted", "AbortError");
      }
      this.root = root;
      return clone2(value);
    }
    close() {
    }
  };
  async function openStorage(name, { open = () => IDBStore.open(name), newClient = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "") } = {}) {
    let storage;
    try {
      storage = await open();
      await storage.transact(() => null);
      return { storage, client: null, warning: null };
    } catch {
      storage?.close();
      return { storage: new MemoryStore(), client: newClient(), warning: "Changes are kept in this tab until the account confirms them." };
    }
  }

  // local/src/outbox.mjs
  var validTarget = (p) => Number.isSafeInteger(p) && p > 0;
  function clearGap(s) {
    delete s.gapStop;
    delete s.blockedReason;
    delete s.expected;
  }
  function gapActive(s) {
    const g = s.gapStop;
    if (g && g.client === s.client && g.inc === s.inc && g.epoch === s.epoch) return true;
    if (g) clearGap(s);
    return false;
  }
  var TransportFailure = class extends Error {
    constructor(cause) {
      super(cause?.message ?? "Transport request failed", { cause });
      this.name = "TransportFailure";
    }
  };
  var definitive = /* @__PURE__ */ new Set(["unknown_client", "gap", "window", "invalid_order", "invalid_ack", "too_large", "paused", "legacy", "incarnation", "exhausted", "not_migrated", "429"]);
  var newKey = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
  function freshClient(s, makeKey) {
    const k = makeKey();
    if (k === s.client || !/^[A-Za-z0-9_-]{22}$/.test(k)) throw Error("invalid_new_client");
    s.client = k;
  }
  function advanceRegistration(s, makeKey) {
    if (s.regN === MAX) {
      freshClient(s, makeKey);
      s.regN = 1;
    } else incCounter(s, "regN");
  }
  var ordered = (s) => Object.keys(s.outbox).map(Number).sort((a, b) => a - b);
  var equal = (a, b) => canonical(a) === canonical(b);
  var ctx = (s) => ({ scope: s.scope, identity: s.identity, inc: s.inc, epoch: s.epoch, client: s.client, regN: s.regN, lease: s.lease.gen, queue: s.queueVersion });
  var owns = (s, c) => s.scope === c.scope && s.identity === c.identity && s.lease.gen === c.lease;
  var incCounter = (obj, k) => {
    if (obj[k] >= MAX) throw Error("exhausted");
    return ++obj[k];
  };
  var tv = (s, p) => s.targetVersions[p] ?? 0;
  var bump = (s, p) => {
    s.activity ?? (s.activity = 0);
    s.targetVersions[p] = incCounter(s, "activity");
  };
  var stamp = (s, o) => {
    s.actionClock ?? (s.actionClock = 0);
    return { t: o.t, p: o.p, ...o.on === void 0 ? {} : { on: o.on }, ...o.cleanup?.length ? { cleanup: clone2(o.cleanup) } : {}, action: o.action ?? incCounter(s, "actionClock") };
  };
  var compactActions = (ops) => [...new Map(ops.map((o) => [o.action, o])).values()].sort((a, b) => a.action - b.action);
  function tidy(s) {
    if (s.notices.length > 100) {
      s.noticeOverflow = Math.min(MAX, (s.noticeOverflow ?? 0) + s.notices.length - 100);
      s.notices = s.notices.slice(-100);
    }
    const pins = /* @__PURE__ */ new Set([...s.rows.map((r) => String(r.p)), ...ordered(s).map((n) => String(s.outbox[n].op.p))]);
    for (const p of Object.keys(s.targetVersions)) if (!pins.has(p)) delete s.targetVersions[p];
  }
  function normalizeQuestions(s, st) {
    const rows = s.rows.filter((r) => r.intent);
    for (let i = 0; i < rows.length; i++) {
      const a = rows[i];
      if (!s.rows.includes(a)) continue;
      for (const b of rows.slice(i + 1)) {
        if (!s.rows.includes(b) || !sameDesign(st, a.p, b.p)) continue;
        a.actions = compactActions([...a.actions ?? [], ...b.actions ?? []]);
        a.intent = chainIntent(a.actions);
        a.cleanup = [.../* @__PURE__ */ new Set([...a.cleanup ?? [], ...b.cleanup ?? []])];
        incCounter(a, "version");
        bump(s, a.p);
        s.rows = s.rows.filter((r) => r !== b);
      }
    }
  }
  var pending = (s, p) => ordered(s).some((n) => sameDesign(s.inst, s.outbox[n].op.p, p));
  function initial(scope, identity, client, st) {
    return { scope, identity, client, inc: st.inc, regN: 1, epoch: null, nextSeq: 1, ackw: 0, inst: clone2(st), outbox: {}, known: {}, rows: [], notices: [], jobs: [], attentionSeq: 0, actionClock: 0, activity: 0, noticeOverflow: 0, targetVersions: {}, queueVersion: 0, lease: { owner: null, gen: 0, until: 0 }, retryAt: 0 };
  }
  function addQuestion(s, p, ops, kind, st, sourceInc, { pendingMatters = false, cleanup = [], force = false } = {}) {
    cleanup = [.../* @__PURE__ */ new Set([...cleanup, ...ops.flatMap((o) => o.cleanup ?? [])])];
    normalizeQuestions(s, st);
    const old = s.rows.find((r) => r.intent && sameDesign(st, r.p, p));
    const actions = compactActions([...old?.actions ?? [], ...ops.map((o) => stamp(s, o))]);
    const intent = chainIntent(actions);
    bump(s, p);
    if (!force && satisfied(intent, st, p) && !(pendingMatters && pending(s, p)) && !cleanup.length) {
      if (old) s.rows = s.rows.filter((r) => r.id !== old.id);
      s.notices.push({ kind: "intent_satisfied", p, intent });
      return null;
    }
    if (old) {
      Object.assign(old, { intent, actions, kind, inc: st.inc, sourceInc, cleanup: [.../* @__PURE__ */ new Set([...old.cleanup ?? [], ...cleanup])] });
      incCounter(old, "version");
      return old.id;
    }
    const id = `${s.client}:attention:${incCounter(s, "attentionSeq")}`;
    s.rows.push({ id, p, intent, actions, cleanup, kind, inc: st.inc, sourceInc, version: 1 });
    return id;
  }
  function append(s, t, p, { on, imp, prov, rv, r, base = s.inst.rev, baseInc = s.inc, retire = true } = {}) {
    const metadata = ["import_done", "rec_ack"].includes(t);
    if (metadata && baseInc !== s.inc) throw Error("stale_import");
    if (baseInc !== s.inc) {
      return addQuestion(s, p, [{ t, p, on }], "stale_view", s.inst, baseInc, { pendingMatters: true });
    }
    if (s.nextSeq > MAX) throw Error("sequence_exhausted");
    if (retire && !metadata) {
      const retired = s.rows.filter((r2) => sameDesign(s.inst, r2.p, p));
      for (const r2 of retired) s.notices.push({ kind: "replaced_intent", p: r2.p, intent: clone2(r2.intent), row: r2.id });
      s.rows = s.rows.filter((r2) => !retired.includes(r2));
    }
    const dep = metadata ? void 0 : ordered(s).filter((n) => s.outbox[n].op.p === p).at(-1);
    const op = { seq: s.nextSeq++, t, ...metadata ? {} : { p }, base };
    for (const [k, v] of Object.entries({ imp, prov, rv, r })) if (v !== void 0) op[k] = v;
    if (on !== void 0) op.on = on;
    if (dep !== void 0) op.dep = dep;
    s.outbox[op.seq] = { op, action: stamp(s, op).action, inc: s.inc, frozen: false, attempts: 0, refused: 0 };
    incCounter(s, "queueVersion");
    bump(s, p);
    return op.seq;
  }
  function adopt(s, st, makeKey = newKey) {
    const groups = /* @__PURE__ */ new Map();
    for (const job of s.jobs) for (const p of job.todo ?? []) {
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push({ t: "save", p, action: job.action ?? 0 });
    }
    for (const n of ordered(s)) {
      const op = { ...s.outbox[n].op, action: s.outbox[n].action, cleanup: s.outbox[n].cleanup };
      if (["import_done", "rec_ack"].includes(op.t)) continue;
      if (!groups.has(op.p)) groups.set(op.p, []);
      groups.get(op.p).push(op);
    }
    for (const [p, ops] of groups) addQuestion(s, p, ops, "recovered_change", st, s.inc);
    clearGap(s);
    for (const j of s.jobs) {
      if (!["done", "declined"].includes(j.status)) {
        j.status = "review";
        j.todo = [];
      }
    }
    s.outbox = {};
    s.known = {};
    s.ackw = 0;
    s.nextSeq = 1;
    s.epoch = null;
    advanceRegistration(s, makeKey);
    incCounter(s.lease, "gen");
    s.lease.owner = null;
    s.lease.until = 0;
    incCounter(s, "queueVersion");
    s.inc = st.inc;
    s.inst = clone2(st);
    normalizeQuestions(s, st);
  }
  function install(s, st, makeKey = newKey) {
    if (st.inc > s.inc) adopt(s, st, makeKey);
    else if (st.inc === s.inc && st.rev >= s.inst.rev) {
      s.inst = clone2(st);
      normalizeQuestions(s, st);
    }
  }
  function confirmationValid(s, row, t) {
    return t.scope === s.scope && t.identity === s.identity && t.inc === s.inc && t.row === row.id && t.rowVersion === row.version && t.targetVersion === tv(s, row.p);
  }
  function substitute(s, op) {
    if (op.dep !== void 0 && s.known[op.dep]) {
      const w = s.known[op.dep].w;
      delete op.dep;
      if (w) op[w[0]] = w[1];
    }
  }
  function request(s, upto = Infinity) {
    let ack = s.ackw;
    while (s.known[ack + 1]) ack++;
    const held = ordered(s).map((n) => s.outbox[n]).filter((e) => e.frozen && e.op.dep !== void 0).map((e) => e.op.dep);
    if (held.length) ack = Math.max(s.ackw, Math.min(ack, Math.min(...held) - 1));
    const ns = ordered(s).filter((n) => n <= upto && n - ack <= 50).slice(0, 50);
    for (const n of ns) {
      const e = s.outbox[n];
      if (!e.frozen) {
        substitute(s, e.op);
        e.frozen = true;
      }
      incCounter(e, "attempts");
    }
    return { wire: { client: s.client, epoch: s.epoch, inc: s.inc, ack, ops: ns.map((n) => clone2(s.outbox[n].op)) }, context: ctx(s) };
  }
  function receive(s, request2, response, now) {
    const { wire: q, context: c } = request2;
    if (!owns(s, c)) return "stale_identity_or_lease";
    if (response.batch === "incarnation" && response.inc > s.inc) return "incarnation";
    if (q.client !== s.client || q.epoch !== s.epoch || q.inc !== s.inc) return "stale_reply";
    if (response.batch) {
      if (definitive.has(response.batch)) {
        for (const o of q.ops) if (s.outbox[o.seq]) incCounter(s.outbox[o.seq], "refused");
      }
      if (response.batch === "503") {
        s.backoff = Math.min(6e4, (s.backoff ?? 500) * 2);
        s.retryAt = Math.max(s.retryAt, now + Math.max(s.backoff, (response.retryAfter ?? 0) * 1e3));
      }
      if (response.batch === "429") s.retryAt = Math.max(s.retryAt, now + Math.max(0, response.retryAfter ?? 60) * 1e3);
      return response.batch;
    }
    s.backoff = 0;
    s.ackw = Math.max(s.ackw, q.ack);
    const affected = /* @__PURE__ */ new Set();
    for (const r of response.results) {
      const e = s.outbox[r.seq];
      if (!e) continue;
      const o = e.op;
      s.known[r.seq] = clone2(r);
      const job = o.imp && s.jobs.find((j) => j.id === o.imp);
      if (job) {
        if (o.t === "save") {
          job.todo = job.todo.filter((p) => String(p) !== String(o.p));
          job.receipts ?? (job.receipts = {});
          job.receipts[o.p] = r.code;
          if (r.code === "S" && r.w?.[0] === "g" && Number.isSafeInteger(r.w[1]) && r.w[1] > 0) {
            job.saved ?? (job.saved = {});
            job.saved[o.p] = r.w[1];
          }
          if (!["S", "n"].includes(r.code)) {
            job.status = r.code === "f" ? "full" : "review";
            job.failed ?? (job.failed = []);
            if (!job.failed.includes(o.p)) job.failed.push(o.p);
          }
        }
        if (o.t === "import_done") {
          job.status = ["n", "S"].includes(r.code) ? "done" : "review";
          job.finalSeq = null;
        }
        if (o.t === "rec_ack") {
          job.ack = ["n", "S"].includes(r.code) ? "done" : "review";
        }
      }
      if (r.code === "s" && (o.t === "remove" || o.t === "next")) s.notices.push({ kind: "superseded", op: clone2(o) });
      else if (["c", "s", "f", "outcome_unknown"].includes(r.code)) {
        affected.add(o.p);
        if (o.t === "relink" && r.code === "c" && !validTarget(r.target)) {
          s.notices.push({ kind: "relink_unresolved", p: o.p });
        } else if (o.t === "relink" && r.code === "c" && validTarget(r.target)) {
          addQuestion(s, r.target, [{ t: "save", p: r.target, action: e.action }], "relink_confirm", response.state, s.inc, { cleanup: [o.p] });
        } else {
          const later = ordered(s).filter((n) => n > r.seq && sameDesign(response.state, s.outbox[n].op.p, o.p)).map((n) => ({ ...s.outbox[n].op, action: s.outbox[n].action, cleanup: s.outbox[n].cleanup }));
          addQuestion(s, o.p, [{ ...o, action: e.action }, ...later], r.code === "c" ? "needs_confirm" : r.code === "f" ? "full" : "uncertain", response.state, s.inc, { pendingMatters: later.length > 0, cleanup: e.cleanup ?? [] });
        }
      } else if (r.code === "i" || r.code === "l") s.notices.push({ kind: r.code === "i" ? "invalid" : "later", op: clone2(o) });
      delete s.outbox[r.seq];
      bump(s, o.p);
      incCounter(s, "queueVersion");
      if (e.cleanup?.length && ["S", "n"].includes(r.code) && response.state.items[o.p]) for (const h of e.cleanup) append(s, "remove", h, { base: e.cleanupBase, baseInc: q.inc, retire: false });
    }
    install(s, response.state);
    for (const row of [...s.rows]) if ([...affected].some((p) => sameDesign(s.inst, p, row.p)) && !pending(s, row.p) && !row.cleanup?.length && satisfied(row.intent, s.inst, row.p)) {
      s.notices.push({ kind: "intent_satisfied", p: row.p, intent: row.intent });
      s.rows = s.rows.filter((r) => r !== row);
    }
    const pins = /* @__PURE__ */ new Set([...ordered(s).map((n) => s.outbox[n].op.dep), ...(s.hold ?? []).flatMap((h) => h.seqs)]);
    for (const n of Object.keys(s.known)) if (+n <= s.ackw && !pins.has(+n)) delete s.known[n];
    return "ok";
  }
  var LocalOutbox = class _LocalOutbox {
    constructor({ storage, clock, transport, scope, identity, tab = "tab", locks = null, makeKey = newKey }) {
      Object.assign(this, { storage, clock, transport, scope, identity, tab, locks, makeKey });
      this.cached = null;
      this.timer = null;
    }
    static async open({ storage, clock, transport, scope, client, tab = "tab", locks = null, makeKey = newKey, state: state2 }) {
      const identity = await storage.transact((root) => {
        if (root.active.scope !== scope) {
          root.active = { scope, generation: root.active.generation + 1 };
        }
        if (!root.streams[scope]) root.streams[scope] = initial(scope, root.active.generation, client, state2);
        root.streams[scope].identity = root.active.generation;
        return root.active.generation;
      });
      return new _LocalOutbox({ storage, clock, transport, scope, identity, tab, locks, makeKey });
    }
    async tx(fn, options = {}) {
      let committed;
      try {
        return await this.storage.transact((root) => {
          if (root.active.scope !== this.scope || root.active.generation !== this.identity) return { stale: true };
          const s = root.streams[this.scope];
          const result = fn(s, root);
          tidy(s);
          committed = clone2(s);
          return result;
        }, options).then((v) => {
          this.cached = committed;
          return v;
        });
      } catch (e) {
        if (!options.abort && !this.storage.volatile && ["QuotaExceededError", "InvalidStateError", "SecurityError", "UnknownError", "NotFoundError"].includes(e.name) && this.cached) {
          const old = this.cached, mem = new MemoryStore(), client = this.makeKey();
          if (client === old.client) throw Error("fresh_memory_key_required");
          const fresh = initial(this.scope, this.identity, client, old.inst);
          fresh.actionClock = old.actionClock;
          fresh.rows = clone2(old.rows);
          fresh.attentionSeq = old.attentionSeq;
          fresh.jobs = clone2(old.jobs);
          for (const job of fresh.jobs) {
            if (!["done", "declined"].includes(job.status)) {
              for (const p of job.todo ?? []) addQuestion(fresh, p, [{ t: "save", p, action: job.action }], "storage_recovery", old.inst, old.inc, { pendingMatters: true });
              job.todo = [];
              job.status = "review";
            }
          }
          for (const n of ordered(old).filter((n2) => old.outbox[n2].op.p !== void 0)) addQuestion(fresh, old.outbox[n].op.p, [{ ...old.outbox[n].op, action: old.outbox[n].action }], "storage_recovery", old.inst, old.inc, { pendingMatters: true, force: true });
          mem.root = { active: { scope: this.scope, generation: this.identity }, streams: { [this.scope]: fresh } };
          this.storage = mem;
          this.cached = clone2(fresh);
          this.warning = "Storage stopped working. Earlier changes remain on this device; review them before sending again. New changes stay in this tab until saved.";
          throw new Error("memory_mode_retry_action", { cause: e });
        }
        throw e;
      }
    }
    async withRenewal(fn) {
      if (this.renewDepth) {
        this.renewDepth++;
        try {
          return await fn();
        } finally {
          this.renewDepth--;
        }
      }
      this.renewDepth = 1;
      const schedule = this.clock.setInterval?.bind(this.clock) ?? globalThis.setInterval, clear = this.clock.clearInterval?.bind(this.clock) ?? globalThis.clearInterval;
      const timer2 = schedule(() => this.renew().catch(() => {
      }), 5e3);
      timer2?.unref?.();
      try {
        return await fn();
      } catch (e) {
        if (e.code === "local_transport_timeout") return "uncertain";
        throw e;
      } finally {
        clear(timer2);
        this.renewDepth = 0;
      }
    }
    async renew() {
      if (this.renewDeadline !== void 0 && this.clock.now() >= this.renewDeadline) return false;
      const own = this.renewContext;
      if (!own) return false;
      return this.tx((s) => {
        if (s.lease.owner !== this.tab || s.lease.gen !== own.lease || s.lease.until <= this.clock.now()) return false;
        s.lease.until = this.clock.now() + 15e3;
        return true;
      });
    }
    async callTransport(method, wire, { sender = false } = {}) {
      const captured = await this.tx((s) => ctx(s));
      if (captured.stale) throw Object.assign(new Error("stale_identity"), { code: "stale_identity" });
      const deadline = this.clock.now() + 6e4;
      if (sender) this.renewDeadline = deadline;
      const schedule = this.clock.setInterval?.bind(this.clock) ?? globalThis.setInterval, clear = this.clock.clearInterval?.bind(this.clock) ?? globalThis.clearInterval;
      const timeout = () => Object.assign(new Error("Local transport request exceeded 60 seconds"), { code: "local_transport_timeout" });
      let timer2;
      const expired = new Promise((_, reject) => {
        timer2 = schedule(() => reject(timeout()), 6e4);
        timer2?.unref?.();
      });
      try {
        const reply = await Promise.race([Promise.resolve().then(() => this.transport[method](wire)).catch((e) => {
          if (e?.code === "scope_mismatch" || e?.code === "legacy" || e?.code === "paused") throw e;
          throw new TransportFailure(e);
        }), expired]);
        if (this.clock.now() >= deadline) throw timeout();
        return reply;
      } catch (e) {
        if (sender && e.code === "local_transport_timeout") await this.tx((s) => {
          if (owns(s, captured) && s.lease.owner === this.tab) {
            s.lease.owner = null;
            s.lease.until = 0;
            incCounter(s.lease, "gen");
          }
        });
        throw e;
      } finally {
        clear(timer2);
        if (sender && this.renewDeadline === deadline) delete this.renewDeadline;
      }
    }
    async inspect() {
      return this.tx((s) => s);
    }
    watch(listener) {
      return this.storage.subscribe?.(() => this.inspect().then(listener).catch(() => {
      })) ?? (() => {
      });
    }
    async append(t, p, opts = {}) {
      return this.tx((s) => {
        if (s.nextSeq > MAX) {
          if (ordered(s).length) throw Error("drain_before_sequence_rotation");
          clearGap(s);
          freshClient(s, this.makeKey);
          s.regN = 1;
          s.epoch = null;
          s.nextSeq = 1;
          s.ackw = 0;
          s.known = {};
          incCounter(s.lease, "gen");
          s.lease.owner = null;
          s.lease.until = 0;
        }
        return append(s, t, p, opts);
      });
    }
    async question(p, ops, kind = "uncertain_ask") {
      return this.tx((s) => addQuestion(s, p, ops, kind, s.inst, s.inc, { pendingMatters: true }));
    }
    async lease() {
      return this.tx((s) => {
        const now = this.clock.now();
        if (s.lease.owner !== this.tab && s.lease.until > now) return false;
        if (s.lease.owner !== this.tab || s.lease.until <= now) {
          incCounter(s.lease, "gen");
          s.lease.owner = this.tab;
        }
        s.lease.until = now + 15e3;
        return ctx(s);
      }).then((c) => {
        if (c && !c.stale) this.renewContext = c;
        return c;
      });
    }
    async release() {
      return this.tx((s) => {
        if (s.lease.owner === this.tab) {
          s.lease.owner = null;
          s.lease.until = 0;
          incCounter(s.lease, "gen");
        }
      });
    }
    async register() {
      return this.withRenewal(() => this.registerInner());
    }
    async registerInner() {
      const owner = await this.lease();
      if (!owner || owner.stale) return "busy";
      const pre = await this.tx((s) => s.epoch !== null ? { registered: true } : { context: ctx(s), wire: { client: s.client, regN: s.regN, inc: s.inc } });
      if (pre.registered) return "ok";
      if (pre.stale) return "stale_identity";
      const r = await this.callTransport("register", pre.wire, { sender: true });
      const verdict = await this.tx((s) => {
        if (!owns(s, pre.context)) return "stale_reply";
        if (r.error === "incarnation" && r.inc > s.inc) return "incarnation";
        if (!equal(ctx(s), pre.context)) return "stale_reply";
        if (r.error) return r.error;
        s.epoch = r.epoch;
        return "ok";
      });
      if (verdict === "incarnation" || verdict === "stale_registration") await this.getState(pre.context, { sender: true });
      return verdict;
    }
    async getState(context, { sender = false } = {}) {
      const captured = context ?? await this.tx((s) => ctx(s));
      if (captured.stale) return "stale_identity";
      const st = await this.callTransport("state", void 0, { sender });
      return this.tx((s) => {
        if (!owns(s, captured)) return "stale_identity_or_lease";
        install(s, st, this.makeKey);
        return "ok";
      });
    }
    async installForHarness(st) {
      return this.tx((s) => install(s, st));
    }
    async recover(captured) {
      const pre = await this.tx((s) => owns(s, captured) && ["inc", "epoch", "regN", "client"].every((k) => ctx(s)[k] === captured[k]) ? ctx(s) : null);
      if (!pre || pre.stale) return "stale_reply";
      const st = await this.callTransport("state", void 0, { sender: true });
      const plan = await this.tx((s) => {
        if (!equal(ctx(s), pre)) return { error: "abandoned_transition" };
        if (st.inc > s.inc) {
          install(s, st, this.makeKey);
          return { error: "adopted" };
        }
        const groups = /* @__PURE__ */ new Map();
        for (const n of ordered(s)) {
          const p = s.outbox[n].op.p;
          if (!groups.has(p)) groups.set(p, []);
          groups.get(p).push(n);
        }
        const keep = [], questions2 = [];
        for (const [p, ns] of groups) {
          if (p === void 0) {
            keep.push(...ns);
            continue;
          }
          if (ns.some((n) => s.outbox[n].attempts > s.outbox[n].refused)) {
            const ops = ns.map((n) => ({ ...clone2(s.outbox[n].op), action: s.outbox[n].action, cleanup: s.outbox[n].cleanup }));
            questions2.push({ p, ops });
          } else keep.push(...ns);
        }
        advanceRegistration(s, this.makeKey);
        return { context: ctx(s), wire: { client: s.client, regN: s.regN, inc: s.inc }, keep, questions: questions2 };
      });
      if (plan.stale || plan.error) return plan.error ?? "stale_identity";
      const r = await this.callTransport("register", plan.wire, { sender: true });
      const verdict = await this.tx((s) => {
        if (!equal(ctx(s), plan.context)) return "abandoned_transition";
        if (r.error === "incarnation" && r.inc > s.inc) return "incarnation";
        if (r.error) return "abandoned_transition";
        const old = plan.keep.sort((a, b) => a - b).map((n) => clone2(s.outbox[n]));
        for (const q of plan.questions) addQuestion(s, q.p, q.ops, "uncertain_ask", st, s.inc);
        const mapped = {}, next = {};
        let seq = 1;
        for (const e of old) {
          const o = e.op;
          if (o.dep !== void 0 && mapped[o.dep] === void 0) {
            substitute(s, o);
            delete o.dep;
          }
          mapped[o.seq] = seq;
          o.seq = seq++;
          if (o.dep !== void 0) o.dep = mapped[o.dep];
          next[o.seq] = { ...e, op: o, action: e.action, inc: s.inc, frozen: false, attempts: 0, refused: 0 };
        }
        clearGap(s);
        s.outbox = next;
        for (const j of s.jobs) {
          if (j.status === "running" && plan.questions.some((q) => q.ops.some((o) => o.imp === j.id))) j.status = "review";
        }
        s.nextSeq = seq;
        s.epoch = r.epoch;
        s.known = {};
        s.ackw = 0;
        incCounter(s, "queueVersion");
        install(s, st, this.makeKey);
        return "moved";
      });
      if (verdict === "incarnation") await this.getState(plan.context, { sender: true });
      return verdict;
    }
    async send({ hooks = {}, abortReceive = false, upto = Infinity } = {}) {
      const run = async () => {
        let s = await this.inspect();
        if (s.stale) return "stale_identity";
        if (s.epoch === null) {
          const v = await this.register();
          if (v !== "ok") return v;
        }
        const own = await this.lease();
        if (!own || own.stale) return "busy";
        const prepared = await this.tx((s2) => s2.lease.owner !== this.tab || s2.lease.gen !== own.lease || s2.lease.until <= this.clock.now() ? { busy: true } : gapActive(s2) ? { blocked: "gap" } : s2.retryAt > this.clock.now() ? { wait: true } : request(s2, upto));
        if (prepared.stale) return "stale_identity";
        if (prepared.busy) return "busy";
        if (prepared.wait) return "waiting";
        if (prepared.blocked) return prepared.blocked;
        if (!prepared.wire.ops.length) return "empty";
        if (hooks.prepared) await hooks.prepared(prepared);
        let response;
        try {
          response = await this.callTransport("ops", prepared.wire, { sender: true });
        } catch (e) {
          if (e instanceof TransportFailure || e.code === "local_transport_timeout") return "uncertain";
          if (e.code === "stale_identity") return "stale_identity";
          throw e;
        }
        if (hooks.sent) await hooks.sent(prepared, response);
        const verdict = await this.tx((s2) => receive(s2, prepared, response, this.clock.now()), { abort: abortReceive });
        if (verdict === "unknown_client") await this.recover(prepared.context);
        else if (verdict === "incarnation" || verdict === "not_migrated") await this.getState(prepared.context, { sender: true });
        else if (verdict === "gap") await this.tx((s2) => {
          if (owns(s2, prepared.context) && ["client", "inc", "epoch"].every((k) => s2[k] === prepared.wire[k])) s2.gapStop = { reason: "gap", client: s2.client, inc: s2.inc, epoch: s2.epoch, expect: response.expect };
        });
        return verdict;
      };
      try {
        return await this.withRenewal(() => this.locks ? this.locks.request(`wishlist:${this.scope}`, run) : run());
      } catch (e) {
        if (e instanceof TransportFailure || e.code === "local_transport_timeout") return "uncertain";
        throw e;
      }
    }
    async drain(max = 100) {
      for (let i = 0; i < max; i++) {
        const s = await this.inspect();
        if (s.stale) return "stale_identity";
        if (!ordered(s).length) return "drained";
        const v = await this.send();
        if (["busy", "waiting", "uncertain", "paused", "legacy", "gap", "not_migrated", "stale_registration", "503"].includes(v)) return v;
      }
      throw Error("drain_limit");
    }
    async display(id, { hooks = {}, abort = false } = {}) {
      const pre = await this.tx((s) => ({ scope: s.scope, identity: s.identity }));
      if (pre.stale) return null;
      const st = await this.callTransport("state");
      if (hooks.read) await hooks.read();
      const shown = await this.tx((s) => {
        if (pre.scope !== s.scope || pre.identity !== s.identity) return null;
        install(s, st, this.makeKey);
        const row = s.rows.find((r) => r.id === id);
        if (!row) return null;
        row.inc = s.inc;
        const token = { scope: s.scope, identity: s.identity, inc: s.inc, row: id, rowVersion: row.version, targetVersion: tv(s, row.p), displayedRev: s.inst.rev, target: targetState(s.inst, row.p) };
        return { row: clone2(row), token };
      }, { abort });
      if (hooks.displayed) await hooks.displayed(shown);
      return shown;
    }
    async confirm(id, yes, token, { hooks = {}, abort = false } = {}) {
      const first = await this.tx((s) => {
        const row = s.rows.find((r) => r.id === id);
        return !row ? "missing" : !confirmationValid(s, row, token) ? "stale_confirmation" : pending(s, row.p) ? "target_pending" : "ok";
      });
      if (first !== "ok") return first;
      const st = await this.callTransport("state");
      if (hooks.reread) await hooks.reread(st);
      if (hooks.beforeCommit) await hooks.beforeCommit();
      const answer = await this.tx((s) => {
        const row = s.rows.find((r) => r.id === id);
        if (!row) return "missing";
        if (!confirmationValid(s, row, token)) return "stale_transition";
        if (pending(s, row.p)) return "target_pending";
        if (st.inc > s.inc) {
          install(s, st, this.makeKey);
          return "needs_reconfirm";
        }
        if (st.inc !== s.inc || row.inc !== s.inc) return "needs_reconfirm";
        if (yes && !equal(targetState(st, row.p), token.target)) {
          install(s, st, this.makeKey);
          incCounter(row, "version");
          return "needs_reconfirm";
        }
        if (yes && !equal(targetState(s.inst, row.p), token.target)) {
          incCounter(row, "version");
          return "needs_reconfirm";
        }
        const actions = [], p = row.p, shown = token.target;
        let has = shown.present, flag = shown.item?.f ?? 0;
        if (yes) {
          const intent = row.intent;
          if (intent.presence === null && !has && intent.nextUp !== null) return "target_absent";
          if (intent.presence === false) {
            if (has) actions.push(["remove"]);
          } else {
            if (intent.presence === true && !has) {
              if (typeof p === "string" && !(shown.gone && shown.gone.to === null)) return "cannot_restore_handle";
              actions.push(["save"]);
              has = true;
              flag = 0;
            }
            if (has && intent.nextUp !== null && flag !== intent.nextUp) actions.push(["next", !!intent.nextUp]);
          }
        }
        let savedSeq;
        for (const [t, on] of actions) {
          const dest = t === "next" ? shown.target : p;
          const seq = append(s, t, dest, { on, base: token.displayedRev, baseInc: token.inc, retire: false });
          if (t === "save") savedSeq = seq;
          if (t === "save" && typeof dest === "string") s.outbox[seq].op.r = shown.gone.rm;
        }
        if (yes && row.cleanup?.length) {
          if (savedSeq) {
            s.outbox[savedSeq].cleanup = clone2(row.cleanup);
            s.outbox[savedSeq].cleanupBase = token.displayedRev;
          } else for (const h of row.cleanup) append(s, "remove", h, { base: token.displayedRev, baseInc: token.inc, retire: false });
        }
        s.rows = s.rows.filter((r) => r.id !== id);
        incCounter(s, "queueVersion");
        return yes ? "queued" : "dismissed";
      }, { abort });
      if (hooks.committed) await hooks.committed(answer);
      return answer;
    }
  };

  // browser/imports.mjs
  var cleanHandles = (raw) => Array.isArray(raw) ? [...new Set(raw.filter((h) => typeof h === "string" && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h)))].slice(0, 1500) : [];
  async function sourceId(source) {
    return [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(source))))].map((x) => x.toString(16).padStart(2, "0")).join("").slice(0, 16);
  }
  function attachPicks(s, j) {
    const done = new Set((j.nextDone || []).map(String));
    let changed = false;
    for (const p of j.next || []) {
      if (done.has(String(p))) continue;
      const row = s.rows.find((r) => r.intent?.presence === true && String(r.p) === String(p));
      if (!row) continue;
      if ((j.todo || []).some((x) => String(x) === String(p)) && j.inc === s.inc) continue;
      addQuestion(s, p, [{ t: "next", p, on: true }], row.kind, s.inst, row.sourceInc ?? j.inc, { pendingMatters: true });
      j.nextDone ?? (j.nextDone = []);
      j.nextDone.push(p);
      done.add(String(p));
      changed = true;
    }
    return changed;
  }
  function queueNext(s, j) {
    attachPicks(s, j);
    const done = new Set((j.nextDone || []).map(String)), pending2 = (j.next || []).filter((p) => !done.has(String(p)));
    if (!pending2.length) return false;
    j.nextDone ?? (j.nextDone = []);
    j.nextSkipped ?? (j.nextSkipped = []);
    let queued = 0;
    for (const p of pending2) {
      if (queued >= 50) break;
      const it = s.inst.items[p], known = j.nextKnown?.[p];
      if (known !== void 0) {
        j.nextDone.push(p);
        if (!it || it.g !== known || it.f === 1) {
          j.nextSkipped.push(p);
          continue;
        }
        append(s, "next", p, { on: true, base: j.base, baseInc: j.inc, retire: false });
        queued++;
        continue;
      }
      const code = j.receipts?.[p];
      if (code === void 0 && (j.todo || []).some((x) => String(x) === String(p))) continue;
      if ((j.failed || []).some((x) => String(x) === String(p))) continue;
      j.nextDone.push(p);
      const own = j.saved?.[p];
      if (code !== "S" || !Number.isSafeInteger(own) || !it || it.g !== own || it.f === 1) {
        j.nextSkipped.push(p);
        continue;
      }
      append(s, "next", p, { on: true, base: own, baseInc: j.inc, retire: false });
      queued++;
    }
    return queued > 0;
  }
  var Imports = class {
    constructor(controller2) {
      this.c = controller2;
      this.busy = false;
    }
    /** Step 2: `next` lists the device handles the guest put in Next up. The choice is part of the offer identity
     * (an offer with different picks is a different offer), so an earlier answer never consumes a later choice.
     * Without picks the identity is exactly the Step 1 one, so existing jobs keep matching. */
    async offer({ kind, handles: handles2, rv = 0, next = [] }) {
      handles2 = cleanHandles(handles2);
      if (!handles2.length) return null;
      const picked = cleanHandles(next).filter((h) => handles2.includes(h)).sort();
      const id = await sourceId(picked.length ? [kind, rv, [...handles2].sort(), ["next", ...picked]] : [kind, rv, [...handles2].sort()]), s = await this.c.inspect();
      if (!s || this.c.blocked || this.c.readOnly) return null;
      if (s.jobs.some((j) => j.id === id) || this.c.imports?.some((x) => x[0] === id)) return null;
      const rows = await this.c.resolve(handles2, "handles");
      if (rows.some((x) => !["listed", "unlisted", "missing", "unavailable"].includes(x.state))) throw Error("import_retry");
      const final = await this.c.inspect();
      if (!final || this.c.blocked || final.inc !== s.inc || final.identity !== s.identity) return null;
      const ids2 = [...new Set(rows.filter((x) => ["listed", "unlisted"].includes(x.state) && Number.isSafeInteger(x.id) && x.id > 0).map((x) => x.id))].filter((p) => !final.inst.items[p]);
      const unresolved = rows.filter((x) => !["listed", "unlisted"].includes(x.state) || !Number.isSafeInteger(x.id) || x.id <= 0).map((x) => x.input);
      const flagged = new Set(picked), nextIds = [], nextKnown = {};
      for (const x of rows) {
        if (!flagged.has(x.input) || !["listed", "unlisted"].includes(x.state) || !Number.isSafeInteger(x.id) || x.id <= 0 || nextIds.includes(x.id)) continue;
        const it = final.inst.items[x.id];
        if (it?.f === 1) continue;
        if (it) nextKnown[x.id] = it.g;
        nextIds.push(x.id);
      }
      return { id, kind, handles: handles2, rv, ids: ids2, unresolved, next: nextIds, nextKnown, base: final.inst.rev, inc: final.inc, identity: final.identity, scope: final.scope };
    }
    async answer(offer, yes) {
      return this.c.outbox.tx((s) => {
        if (s.identity !== offer.identity || s.scope !== offer.scope || s.inc !== offer.inc) return "changed";
        if (s.jobs.some((j) => j.id === offer.id)) return "already_answered";
        if (s.actionClock >= 999999999999) throw Error("exhausted");
        s.jobs.push({ ...structuredClone(offer), todo: yes ? [...offer.ids] : [], next: yes ? [...offer.next || []] : [], nextDone: [], nextSkipped: [], status: yes ? "running" : "declined", action: ++s.actionClock, receipts: {} });
        return yes ? "queued" : "declined";
      });
    }
    async pump() {
      if (this.busy || this.c.blocked || this.c.readOnly) return false;
      this.busy = true;
      try {
        return await this.c.outbox.tx((s) => {
          if (Object.keys(s.outbox).length) return false;
          for (const j of s.jobs) {
            if (j.inc !== s.inc || j.scope !== s.scope) {
              if (j.scope === s.scope) attachPicks(s, j);
              continue;
            }
            if (j.status === "running") {
              if (j.todo.length) {
                for (const p of j.todo.slice(0, 50)) append(s, "save", p, { base: j.base, baseInc: j.inc, imp: j.id, prov: j.rv, retire: false });
                return true;
              }
              if (queueNext(s, j)) return true;
              if (j.unresolved.length) {
                j.status = "unresolved";
                continue;
              }
              j.status = "finishing";
              j.finalSeq = append(s, "import_done", void 0, { imp: j.id, base: j.base, baseInc: j.inc });
              return true;
            }
            if (j.status === "done" && j.kind === "recovery" && j.rv && !j.ack) {
              j.ack = "pending";
              append(s, "rec_ack", void 0, { imp: j.id, rv: j.rv, base: j.base, baseInc: j.inc });
              return true;
            }
            if (["full", "review", "reviewed", "unresolved", "done"].includes(j.status) && queueNext(s, j)) return true;
          }
          return false;
        });
      } finally {
        this.busy = false;
      }
    }
    async review(id) {
      return this.c.outbox.tx((s) => {
        const j = s.jobs.find((x) => x.id === id);
        if (!j || !["full", "review"].includes(j.status)) return false;
        for (const p of /* @__PURE__ */ new Set([...j.todo || [], ...j.failed || []])) addQuestion(s, p, [{ t: "save", p, action: j.action }], "import_reask", s.inst, j.inc, { pendingMatters: true });
        j.todo = [];
        j.failed = [];
        attachPicks(s, j);
        j.status = j.unresolved.length ? "unresolved" : "reviewed";
        return true;
      });
    }
    async dismiss(id) {
      return this.c.outbox.tx((s) => {
        const j = s.jobs.find((x) => x.id === id);
        if (j) j.dismissed = true;
      });
    }
    async recheck(id) {
      const before = await this.c.inspect(), job = before?.jobs.find((j) => j.id === id);
      if (!job || !job.unresolved.length) return false;
      const rows = await this.c.resolve(job.unresolved, "handles");
      return this.c.outbox.tx((s) => {
        if (s.identity !== before.identity || s.inc !== before.inc) return false;
        const j = s.jobs.find((x) => x.id === id);
        if (!j) return false;
        const resolved = rows.filter((x) => ["listed", "unlisted"].includes(x.state) && Number.isSafeInteger(x.id) && x.id > 0);
        for (const r of resolved) addQuestion(s, r.id, [{ t: "save", p: r.id, action: j.action }], "import_reask", s.inst, j.inc, { pendingMatters: true });
        j.unresolved = j.unresolved.filter((h) => !resolved.some((r) => r.input === h));
        j.status = j.unresolved.length ? "unresolved" : "reviewed";
        return true;
      });
    }
    async relink() {
      if (this.c.blocked || this.c.readOnly) return false;
      const s = await this.c.inspect();
      if (!s) return false;
      const now = Date.now(), targets = Object.keys(s.inst.items).filter((p) => p.startsWith("h:") && now - (s.relinkAt?.[p] ?? 0) > 864e5);
      if (!targets.length) return false;
      const result = await this.c.resolve(targets.map((p) => p.slice(2)), "handles");
      return this.c.outbox.tx((v) => {
        if (v.inc !== s.inc || v.identity !== s.identity) return false;
        let changed = false;
        v.relinkAt ?? (v.relinkAt = {});
        for (const r of result) {
          const p = "h:" + r.input;
          if (!["listed", "unlisted"].includes(r.state) || !v.inst.items[p] || now - (v.relinkAt[p] ?? 0) <= 864e5) continue;
          v.relinkAt[p] = now;
          append(v, "relink", p, { base: s.inst.rev, baseInc: s.inc, retire: false });
          changed = true;
        }
        return changed;
      });
    }
  };

  // browser/device.mjs
  var DeviceStore = class _DeviceStore {
    constructor(db) {
      this.db = db;
      this.memory = { guest: {}, rev: 0, copied: [], sets: [] };
      this.volatile = !db;
    }
    static async open(name = "paintly-wishlist-device-v2") {
      let db;
      try {
        db = await new Promise((resolve, reject) => {
          const q = indexedDB.open(name, 1);
          q.onupgradeneeded = () => q.result.createObjectStore("device");
          q.onerror = () => reject(q.error);
          q.onsuccess = () => resolve(q.result);
        });
        const store = new _DeviceStore(db);
        await store.tx(() => null);
        return store;
      } catch {
        db?.close();
        return new _DeviceStore(null);
      }
    }
    tx(fn) {
      if (!this.db) {
        const data = structuredClone(this.memory), v = fn(data);
        this.memory = data;
        return Promise.resolve(structuredClone(v));
      }
      return new Promise((resolve, reject) => {
        let tx;
        try {
          tx = this.db.transaction("device", "readwrite", { durability: "strict" });
          if (tx.durability !== "strict") {
            tx.abort();
            throw Error("strict_unavailable");
          }
        } catch (e) {
          reject(e);
          return;
        }
        let val, thrown;
        const s = tx.objectStore("device"), q = s.get("state");
        q.onsuccess = () => {
          try {
            const d = q.result ?? { guest: {}, rev: 0, copied: [], sets: [] };
            val = fn(d);
            if (val?.then) throw Error("async_reducer");
            s.put(d, "state");
          } catch (e) {
            thrown = e;
            tx.abort();
          }
        };
        tx.oncomplete = () => resolve(structuredClone(val));
        tx.onabort = () => reject(thrown ?? tx.error ?? Error("aborted"));
      });
    }
    async capture(raw) {
      const handles2 = cleanHandles(raw), id = await sourceId(handles2.sort());
      return this.tx((d) => {
        const added = handles2.filter((h) => !d.copied.includes(h));
        if (added.length) {
          d.sets.push({ id, handles: added });
          d.copied = [.../* @__PURE__ */ new Set([...d.copied, ...added])];
        }
        return d.sets;
      });
    }
    async guestAdopt() {
      return this.tx((d) => {
        d.guestImported ?? (d.guestImported = []);
        for (const set of d.sets) {
          if (d.guestImported.includes(set.id)) continue;
          for (const h of set.handles) if (Object.keys(d.guest).length < 1500 && !d.guest["h:" + h]) d.guest["h:" + h] = { g: ++d.rev, f: 0 };
          d.guestImported.push(set.id);
        }
        return d;
      });
    }
    async guestAction(t, p) {
      return this.tx((d) => {
        if (t === "remove") {
          delete d.guest[p];
          d.rev++;
        } else if (!d.guest[p]) {
          if (Object.keys(d.guest).length >= 1500) throw Error("full");
          d.guest[p] = { g: ++d.rev, f: 0 };
        }
        return d;
      });
    }
    /** Step 2, guests: the Next up flag lives on the same device entry ({g, f}). */
    async guestFlag(p, on) {
      return this.tx((d) => {
        const e = d.guest[p];
        if (e && e.f !== (on ? 1 : 0)) {
          e.f = on ? 1 : 0;
          d.rev++;
        }
        return d;
      });
    }
    /** Step 2 bulk Next up for guests: one transaction for the whole selection. */
    async guestFlagMany(ps, on) {
      return this.tx((d) => {
        let n = 0;
        for (const p of ps) {
          const e = d.guest[p];
          if (e && e.f !== (on ? 1 : 0)) {
            e.f = on ? 1 : 0;
            n++;
          }
        }
        if (n) d.rev++;
        return { data: d, changed: n };
      });
    }
    async guestRemoveMany(ps) {
      return this.tx((d) => {
        const removed = [];
        for (const p of ps) {
          const e = d.guest[p];
          if (e) {
            removed.push([p, { ...e }]);
            delete d.guest[p];
          }
        }
        if (removed.length) d.rev++;
        return { data: d, removed };
      });
    }
    /** Undo puts entries back with their original order and flag, never over a newer save and never past the cap. */
    async guestRestore(entries) {
      return this.tx((d) => {
        let n = 0;
        for (const [p, e] of entries) {
          if (d.guest[p] || Object.keys(d.guest).length >= 1500) continue;
          d.guest[p] = { g: Number.isSafeInteger(e?.g) ? e.g : ++d.rev, f: e?.f === 1 ? 1 : 0 };
          n++;
        }
        if (n) d.rev++;
        return { data: d, restored: n };
      });
    }
    read() {
      return this.tx((d) => d);
    }
    close() {
      this.db?.close();
    }
  };

  // browser/read.mjs
  async function boundedRead(fetcher, url, options, consume, { ms = 2e4, timers = globalThis } = {}) {
    const abort = new AbortController();
    let timer2, expired = false;
    const timeout = new Promise((_, reject) => {
      timer2 = timers.setTimeout(() => {
        expired = true;
        abort.abort();
        reject(Object.assign(Error("read_timeout"), { code: "read_timeout" }));
      }, ms);
    });
    try {
      return await Promise.race([(async () => {
        const response = await fetcher(url, { ...options, signal: abort.signal });
        const value = await consume(response);
        if (expired) throw Object.assign(Error("read_timeout"), { code: "read_timeout" });
        return value;
      })(), timeout]);
    } finally {
      timers.clearTimeout(timer2);
    }
  }

  // browser/cards.mjs
  var Cards = class {
    constructor({ fetcher, controller: controller2, root = "/", country = "US", readOptions = {} }) {
      Object.assign(this, { fetcher: fetcher ?? controller2?.fetcher ?? ((...args) => globalThis.fetch(...args)), controller: controller2, root, country, readOptions });
      this.cache = /* @__PURE__ */ new Map();
      this.pending = /* @__PURE__ */ new Map();
    }
    parse(html, wanted) {
      const doc = new DOMParser().parseFromString(html, "text/html"), out = /* @__PURE__ */ new Map();
      for (const node2 of doc.querySelectorAll(".pw-item")) {
        const tag = node2.querySelector("script[data-pw-meta]");
        let meta;
        try {
          meta = JSON.parse(tag?.textContent ?? "null");
        } catch {
        }
        if (!meta || !wanted.has(String(meta.id)) || typeof meta.handle !== "string" || !meta.title) continue;
        for (const el2 of node2.querySelectorAll("script,iframe,object,embed,form")) el2.remove();
        for (const el2 of [node2, ...node2.querySelectorAll("*")]) for (const a of [...el2.attributes]) if (a.name.startsWith("on") || ["href", "src", "action"].includes(a.name) && /^\s*javascript:/i.test(a.value)) el2.removeAttribute(a.name);
        node2.dataset.pid = String(meta.id);
        out.set(String(meta.id), { node: node2, meta });
      }
      return out;
    }
    async one(p, handle, options = {}) {
      const k = String(p);
      if (this.pending.has(k)) return this.pending.get(k);
      const run = (async () => {
        let found = handle;
        if (!found) {
          const r2 = (await this.controller.resolve([Number(p)], "ids", this.country))[0];
          if (!["listed", "unlisted"].includes(r2.state)) return { status: r2.state };
          found = r2.handle;
        }
        const { r, text } = await boundedRead(this.fetcher, this.root + "products/" + encodeURIComponent(found) + "?section_id=pw-card", { cache: "no-store", credentials: "same-origin" }, async (r2) => ({ r: r2, text: r2.ok ? await r2.text() : "" }), { ...this.readOptions, ...options });
        if (r.status === 404) {
          const q = (await this.controller.resolve([Number(p)], "ids", this.country, true))[0];
          if (["listed", "unlisted"].includes(q.state) && q.handle && q.handle !== found) {
            const html = await boundedRead(this.fetcher, this.root + "products/" + encodeURIComponent(q.handle) + "?section_id=pw-card", { cache: "no-store", credentials: "same-origin" }, async (r2) => r2.ok ? await r2.text() : "", { ...this.readOptions, ...options });
            return this.parse(html, /* @__PURE__ */ new Set([k])).get(k) ?? { status: "unknown" };
          }
          return { status: ["listed", "unlisted"].includes(q.state) ? "unknown" : q.state };
        }
        if (!r.ok) return { status: "unknown" };
        return this.parse(text, /* @__PURE__ */ new Set([k])).get(k) ?? { status: "unknown" };
      })().finally(() => this.pending.delete(k));
      this.pending.set(k, run);
      return run;
    }
    async batch(ids2) {
      const result = /* @__PURE__ */ new Map(), missing = [];
      for (const p of ids2) {
        const v = this.cache.get(String(p));
        if (v) result.set(String(p), { ...v, node: v.node.cloneNode(true) });
        else missing.push(p);
      }
      for (let i = 0; i < missing.length; i += 24) {
        const part = missing.slice(i, i + 24), wanted = new Set(part.map(String));
        let found = /* @__PURE__ */ new Map(), readFailed = false;
        try {
          const query = part.map((p) => "id:" + p).join(" OR "), u = this.root + "search?" + new URLSearchParams({ q: query, type: "product", "options[unavailable_products]": "show", section_id: "pw-batch" });
          const html = await boundedRead(this.fetcher, u, { credentials: "same-origin", cache: "no-store" }, async (r) => {
            if (!r.ok) throw Error("batch_unavailable");
            return r.text();
          }, this.readOptions);
          found = this.parse(html, wanted);
        } catch {
          readFailed = true;
        }
        const absent = part.filter((p) => !found.has(String(p)));
        let resolved = /* @__PURE__ */ new Map();
        if (!readFailed && absent.length) {
          try {
            const rows = await this.controller.resolve(absent, "ids", this.country);
            resolved = new Map(rows.map((r) => [String(r.input), r]));
          } catch {
            readFailed = true;
          }
        }
        const deadline = Date.now() + (this.readOptions.ms ?? 2e4);
        let cursor = 0;
        await Promise.all(Array.from({ length: Math.min(4, part.length) }, async () => {
          while (cursor < part.length) {
            const p = part[cursor++], k = String(p);
            let v = found.get(k);
            if (!v) {
              const r = resolved.get(k);
              if (readFailed || Date.now() >= deadline) v = { status: "unknown" };
              else if (!r || !["listed", "unlisted"].includes(r.state)) v = { status: r?.state ?? "unknown" };
              else try {
                v = await this.one(p, r.handle, { ms: Math.max(1, deadline - Date.now()) });
              } catch {
                v = { status: "unknown" };
              }
            }
            if (v.node) this.cache.set(k, { ...v, node: v.node.cloneNode(true) });
            result.set(k, v);
          }
        }));
      }
      return result;
    }
  };

  // browser/step2-core.mjs
  var SORTS = ["new", "old", "price-asc", "price-desc", "az"];
  var CRAFTS = ["all", "pbn", "dp"];
  function craftOf(meta) {
    if (!meta || typeof meta.title !== "string") return null;
    const type = String(meta.type || "").toLowerCase().trim(), title = meta.title.toLowerCase().trim();
    if (type.startsWith("diamond painting") || /\(\s*[^)]*diamond painting\s*\)\s*$/.test(title) || title.endsWith("diamond painting")) return "dp";
    if (type.startsWith("paint by numbers") || title.includes("paint by numbers")) return "pbn";
    return "other";
  }
  function fold(value) {
    return String(value ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[‘’“”'"]/g, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  }
  function matches(meta, query) {
    const words = fold(query).split(" ").filter(Boolean);
    if (!words.length) return true;
    if (!meta || typeof meta.title !== "string") return false;
    const hay = fold(meta.title) + " " + fold(String(meta.handle || "").replaceAll("-", " "));
    return words.every((w) => hay.includes(w));
  }
  function isActive(view) {
    return !!(fold(view.q) || view.craft && view.craft !== "all" || ["price-asc", "price-desc", "az"].includes(view.sort));
  }
  function isFiltered(view) {
    return !!(fold(view.q) || view.craft && view.craft !== "all");
  }
  function viewList(ids2, view, metaOf) {
    const q = fold(view.q), craft = CRAFTS.includes(view.craft) ? view.craft : "all", sort = SORTS.includes(view.sort) ? view.sort : "new";
    const filtering = !!q || craft !== "all";
    let waiting = 0, unchecked = 0;
    const kept = [];
    ids2.forEach((id, index) => {
      const meta = metaOf(id), loaded = !!(meta && typeof meta.title === "string");
      if (filtering) {
        if (!meta) {
          waiting++;
          return;
        }
        if (!loaded) {
          unchecked++;
          return;
        }
        if (q && !matches(meta, q)) return;
        if (craft !== "all" && craftOf(meta) !== craft) return;
      }
      kept.push({ id, index, meta: loaded ? meta : null });
    });
    const price = (x) => x.meta && Number.isFinite(x.meta.price) ? x.meta.price : null;
    const byIndex = (a, b) => a.index - b.index;
    if (sort === "old") kept.sort((a, b) => b.index - a.index);
    else if (sort === "price-asc" || sort === "price-desc") {
      const dir = sort === "price-asc" ? 1 : -1;
      kept.sort((a, b) => {
        const x = price(a), y = price(b);
        if (x === null && y === null) return byIndex(a, b);
        if (x === null) return 1;
        if (y === null) return -1;
        return (x - y) * dir || byIndex(a, b);
      });
    } else if (sort === "az") {
      kept.sort((a, b) => {
        if (!a.meta && !b.meta) return byIndex(a, b);
        if (!a.meta) return 1;
        if (!b.meta) return -1;
        return a.meta.title.localeCompare(b.meta.title, void 0, { sensitivity: "base", numeric: true }) || byIndex(a, b);
      });
    }
    if (!filtering && sort !== "new" && sort !== "old") waiting = ids2.filter((id) => !metaOf(id)).length;
    return { list: kept.map((x) => x.id), waiting, unchecked, total: ids2.length };
  }
  function nextUpIds(ids2, flagOf) {
    return ids2.filter((id) => flagOf(id) === 1);
  }
  function pruneSelection(selected, list) {
    const keep = new Set(list.map(String));
    return new Set([...selected].filter((id) => keep.has(String(id))));
  }
  function orderedText(date, { locale = "en", now = /* @__PURE__ */ new Date(), template = "Ordered {date}", fallback: fallback2 = "Ordered" } = {}) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || ""));
    if (!m) return fallback2;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    if (Number.isNaN(d.getTime()) || d.getUTCMonth() !== +m[2] - 1 || d.getUTCDate() !== +m[3]) return fallback2;
    const sameYear = +m[1] === now.getUTCFullYear();
    let text;
    try {
      text = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", ...sameYear ? {} : { year: "numeric" }, timeZone: "UTC" }).format(d);
    } catch {
      text = m[3] + "/" + m[2] + "/" + m[1];
    }
    return template.replace("{date}", text);
  }
  function parseMarks(body, savedIds) {
    const out = /* @__PURE__ */ new Map();
    if (!body || body.v !== 1 || !Array.isArray(body.marks)) return out;
    const saved = new Set(savedIds.map(String));
    for (const row of body.marks) {
      if (!Array.isArray(row) || row.length !== 2) continue;
      const [p, date] = row;
      if (!Number.isSafeInteger(p) || p <= 0 || !saved.has(String(p))) continue;
      if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      out.set(String(p), date);
    }
    return out;
  }
  function countText(tr2, base, count, extra = {}) {
    const key2 = count === 1 && tr2(base + "_one") !== base + "_one" ? base + "_one" : base;
    let s = tr2(key2);
    for (const [k, v] of Object.entries({ count, ...extra })) s = s.replaceAll("{" + k + "}", String(v));
    return s;
  }

  // browser/step2-ui.mjs
  var DEFAULT_TEXT = {
    next_up: "Shortlist",
    next_up_intro: "Your top picks from your saved kits. They stay at the top of your wishlist.",
    next_up_empty: "Tap \u201C+ Shortlist\u201D on any saved kit\u2019s picture to add it here.",
    next_up_add: "Shortlist",
    next_up_added: "Shortlist",
    next_up_add_label: "Add {title} to your shortlist",
    next_up_remove_label: "Take {title} off your shortlist",
    search_label: "Search saved kits",
    search_placeholder: "Search your saved kits",
    filter_label: "Show",
    filter_all: "All",
    filter_pbn: "Paint by numbers",
    filter_dp: "Diamond painting",
    sort_label: "Sort",
    sort_new: "Newest saved",
    sort_old: "Oldest saved",
    sort_price_asc: "Price: low to high",
    sort_price_desc: "Price: high to low",
    sort_az: "A to Z",
    results_all: "{count} designs",
    results_all_one: "1 design",
    results_some: "{shown} of {count} designs",
    clear_filters: "Clear filters",
    no_match: "No saved kits match. Try another search or filter.",
    checking: "Checking {count} more saved designs\u2026",
    checking_one: "Checking 1 more saved design\u2026",
    unchecked: "{count} saved designs couldn\u2019t be checked, so they aren\u2019t in these results.",
    unchecked_one: "1 saved design couldn\u2019t be checked, so it isn\u2019t in these results.",
    select: "Select kits",
    select_done: "Done",
    selected_count: "{count} selected",
    select_all: "Select all {count}",
    select_none: "Clear selection",
    select_item: "Select {title}",
    bulk_next: "Add to shortlist",
    bulk_next_off: "Take off shortlist",
    bulk_remove: "Remove",
    bulk_removed: "{count} designs removed.",
    bulk_removed_one: "1 design removed.",
    bulk_restoring: "Putting {count} designs back\u2026",
    ordered: "Ordered",
    ordered_on: "Ordered {date}",
    saved_design: "Saved design",
    unavailable: "No longer available",
    undo: "Undo",
    close_undo: "Close",
    bulk_failed: "That didn\u2019t go through, so nothing changed. Please try again.",
    ordered_checking: "Checking your past orders for Ordered labels\u2026",
    ordered_incomplete: "We couldn\u2019t check all your past orders yet, so some Ordered labels may be missing.",
    ordered_retry: "Try again",
    import_next: "Includes {count} shortlist picks.",
    import_next_one: "Includes 1 shortlist pick.",
    import_next_only: "Keep your {count} shortlist picks from this device in your account?",
    import_next_only_one: "Keep your shortlist pick from this device in your account?",
    select_prompt: "Tap kits to select",
    select_howto_lead: "Tap the kits you want.",
    select_howto: "Then use the bar at the bottom to add them to your shortlist or remove them.",
    choose_options: "Choose options"
  };
  function step2Text(cfgText = {}, doc = globalThis.document) {
    let page = {};
    try {
      page = JSON.parse(doc?.getElementById("pw-s2-text")?.textContent || "{}") || {};
    } catch {
    }
    const merged = { ...DEFAULT_TEXT };
    for (const [k, v] of Object.entries({ ...cfgText, ...page })) if (typeof v === "string" && v && !/^translation missing/i.test(v)) merged[k] = v;
    return (key2) => merged[key2] ?? key2;
  }
  var SVG = {
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-10"/></svg>',
    search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.6-3.6"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    box: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="4.5"/><path d="m8 12.2 2.8 2.8L16.2 9"/></svg>',
    right: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>'
  };
  function setText(node2, text) {
    if (node2 && node2.textContent !== text) node2.textContent = text;
  }
  var CARD_SUFFIX = / \((?:Diamond Painting|Paint by Numbers|Custom PBN|Wall Art|(?:80|120) Colou?r Paint by Numbers|(?:80|120|150) Colou?rs|150 Colou?r Diamond Painting|Board Canvas)\)/g;
  var cardTitle = (t) => String(t).replace(CARD_SUFFIX, "");
  function el(doc, tag, attrs = {}, text) {
    const n = doc.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === void 0 || v === null || v === false) continue;
      if (k === "class") n.className = v;
      else if (k === "html") n.innerHTML = v;
      else n.setAttribute(k, v === true ? "" : String(v));
    }
    if (text !== void 0) n.textContent = text;
    return n;
  }
  var fill = (s, vars) => Object.entries(vars).reduce((a, [k, v]) => a.replaceAll("{" + k + "}", String(v)), s);
  var WishlistStep2 = class {
    /**
     * host: {
     *   ids(): saved ids, newest first
     *   metaOf(id): product meta | {status} | null
     *   flagOf(id): 0 | 1
     *   canNext(id): boolean (the design can be put in Next up)
     *   orderedOf(id): 'yyyy-mm-dd' | undefined
     *   urlOf(id): product url | undefined
     *   setNext(id, on): Promise
     *   bulkNext(ids, on): Promise
     *   bulkRemove(ids): Promise
     *   undo(): Promise, undoState(): null | {count, busy}
     *   dismissUndo()
     *   repaint(): re-render the grid (resets nothing)
     *   ensureMeta(ids, reason): start loading cards for these ids (non-blocking)
     *   prepare(ids): load cards for these ids and resolve when done (used before bulk Next up)
     *   canEdit(): false while the list is read-only (paused account, signed out)
     * }
     */
    constructor({ tr: tr2, host, doc = globalThis.document, locale = "en", ordered: ordered2 = false }) {
      Object.assign(this, { tr: tr2, host, doc, locale, ordered: ordered2 });
      this.view = { q: "", craft: "all", sort: "new" };
      this.selecting = false;
      this.selected = /* @__PURE__ */ new Set();
      this.last = { list: [], waiting: 0, unchecked: 0, total: 0 };
      this.mounted = false;
      this.timer = null;
    }
    /* ── markup ── */
    mount() {
      if (this.mounted) return;
      const d = this.doc, tr2 = this.tr, grid = d.getElementById("pw-wishlist-grid");
      if (!grid) return;
      this.mounted = true;
      let tools = d.getElementById("pw-s2-tools");
      if (!tools) {
        tools = el(d, "div", { id: "pw-s2-tools" });
        grid.before(tools);
      }
      tools.className = "pw-s2-tools";
      tools.hidden = true;
      tools.replaceChildren();
      const row1 = el(d, "div", { class: "pw-s2-tools__row" });
      const search = el(d, "label", { class: "pw-s2-search" });
      search.append(el(d, "span", { class: "pw-s2-vh" }, tr2("search_label")), el(d, "span", { class: "pw-s2-search__icon", html: SVG.search }));
      this.q = el(d, "input", { type: "search", id: "pw-s2-q", placeholder: tr2("search_placeholder"), autocomplete: "off", autocapitalize: "none", spellcheck: "false", enterkeyhint: "search", maxlength: "80" });
      search.append(this.q);
      const sortWrap = el(d, "label", { class: "pw-s2-sort" });
      sortWrap.append(el(d, "span", { class: "pw-s2-sort__label" }, tr2("sort_label")));
      this.sort = el(d, "select", { id: "pw-s2-sort" });
      for (const [v, k] of [["new", "sort_new"], ["old", "sort_old"]]) this.sort.append(el(d, "option", { value: v }, tr2(k)));
      sortWrap.append(this.sort);
      this.chips = el(d, "div", { class: "pw-s2-chips", role: "group", "aria-label": tr2("filter_label") });
      for (const [v, k] of [["all", "filter_all"], ["pbn", "filter_pbn"], ["dp", "filter_dp"]]) this.chips.append(el(d, "button", { type: "button", class: "pw-s2-chip", "data-s2-craft": v, "aria-pressed": String(v === "all") }, tr2(k)));
      this.selectBtn = el(d, "button", { type: "button", class: "pw-s2-select", "aria-pressed": "false" });
      this.paintSelect();
      row1.append(sortWrap, this.selectBtn);
      const result = el(d, "p", { class: "pw-s2-result" });
      this.resultText = el(d, "span", { id: "pw-s2-result", "aria-live": "polite" });
      this.clear = el(d, "button", { type: "button", class: "pw-s2-clear", hidden: true }, tr2("clear_filters"));
      result.append(this.resultText, this.clear);
      this.note = el(d, "p", { class: "pw-s2-note", hidden: true });
      this.noMatch = el(d, "p", { class: "pw-s2-nomatch", hidden: true, role: "status" }, tr2("no_match"));
      this.orderedNote = el(d, "p", { class: "pw-s2-onote", hidden: true, "aria-live": "polite" });
      this.howto = el(d, "p", { class: "pw-s2-howto", hidden: true, role: "note" });
      this.howto.innerHTML = SVG.box + "<span><b></b> <span></span></span>";
      this.howto.querySelector("b").textContent = tr2("select_howto_lead");
      this.howto.querySelector("b + span").textContent = tr2("select_howto");
      tools.append(row1, result, this.howto, this.note, this.orderedNote, this.noMatch);
      this.tools = tools;
      let undo2 = d.getElementById("pw-s2-undo");
      if (!undo2) {
        undo2 = el(d, "div", { id: "pw-s2-undo" });
        tools.before(undo2);
      }
      undo2.className = "pw-s2-undo";
      undo2.setAttribute("role", "status");
      undo2.setAttribute("aria-live", "polite");
      undo2.hidden = true;
      undo2.replaceChildren();
      this.undoBox = undo2;
      let bar = d.getElementById("pw-s2-selbar");
      if (!bar) {
        bar = el(d, "div", { id: "pw-s2-selbar" });
        const slot = grid.parentNode.querySelector(":scope > .pw-ai-slot");
        if (slot) slot.before(bar);
        else grid.parentNode.append(bar);
      }
      bar.className = "pw-s2-selbar";
      bar.setAttribute("role", "region");
      bar.setAttribute("aria-label", tr2("select"));
      bar.hidden = true;
      bar.replaceChildren();
      this.selCount = el(d, "span", { class: "pw-s2-selbar__count", "aria-live": "polite" });
      this.selAll = el(d, "button", { type: "button", class: "pw-s2-btn", "data-s2-bulk": "all" });
      this.selNext = el(d, "button", { type: "button", class: "pw-s2-btn pw-s2-btn--next", "data-s2-bulk": "next" }, tr2("bulk_next"));
      this.selNextOff = el(d, "button", { type: "button", class: "pw-s2-btn", "data-s2-bulk": "next-off" }, tr2("bulk_next_off"));
      this.selRemove = el(d, "button", { type: "button", class: "pw-s2-btn pw-s2-btn--remove", "data-s2-bulk": "remove" }, tr2("bulk_remove"));
      this.selDone = el(d, "button", { type: "button", class: "pw-s2-btn pw-s2-btn--done", "data-s2-bulk": "done" }, tr2("select_done"));
      const actions = el(d, "div", { class: "pw-s2-selbar__actions" });
      actions.append(this.selAll, this.selNext, this.selNextOff, this.selRemove, this.selDone);
      this.selStatus = el(d, "p", { class: "pw-s2-selbar__status", role: "alert", hidden: true });
      bar.append(this.selCount, actions, this.selStatus);
      this.selbar = bar;
      let next = d.getElementById("pw-s2-next");
      if (!next) {
        next = el(d, "section", { id: "pw-s2-next" });
        d.querySelector(".pw-sechead")?.before(next);
      }
      next.className = "pw-s2-next";
      next.hidden = true;
      next.setAttribute("aria-labelledby", "pw-s2-next-title");
      this.nextBox = next;
      this.q.addEventListener("input", () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.setView({ q: this.q.value }), 160);
      });
      this.q.addEventListener("search", () => {
        clearTimeout(this.timer);
        this.setView({ q: this.q.value });
      });
      this.q.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          clearTimeout(this.timer);
          this.setView({ q: this.q.value });
        }
      });
      this.sort.addEventListener("change", () => this.setView({ sort: this.sort.value }));
      this.chips.addEventListener("click", (e) => {
        const b = e.target.closest("[data-s2-craft]");
        if (b) this.setView({ craft: b.dataset.s2Craft });
      });
      this.clear.addEventListener("click", () => {
        this.q.value = "";
        this.setView({ q: "", craft: "all" });
        this.q.focus();
      });
      this.selectBtn.addEventListener("click", () => this.setSelecting(!this.selecting));
      bar.addEventListener("click", (e) => {
        const b = e.target.closest("[data-s2-bulk]");
        if (b && !b.disabled) this.bulk(b.dataset.s2Bulk);
      });
      this.undoBox.addEventListener("click", (e) => {
        const b = e.target.closest("[data-s2-undo]");
        if (!b || b.disabled) return;
        if (b.dataset.s2Undo === "undo") {
          b.disabled = true;
          Promise.resolve(this.host.undo()).then(() => this.host.repaint()).finally(() => this.focusAfterUndo());
        } else {
          Promise.resolve(this.host.dismissUndo()).finally(() => this.focusAfterUndo());
        }
      });
      this.orderedNote.addEventListener("click", (e) => {
        if (e.target.closest("[data-s2-ordered-retry]")) this.host.retryOrdered?.();
      });
      grid.addEventListener("click", (e) => {
        const pick = e.target.closest(".pw-s2-pick");
        if (pick) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleSelected(pick.closest("[data-s2p]")?.dataset.s2p);
          return;
        }
        const nx = e.target.closest("[data-s2-next]");
        if (nx) {
          e.preventDefault();
          e.stopPropagation();
          const id = nx.closest("[data-s2p]")?.dataset.s2p;
          if (id !== void 0 && nx.getAttribute("aria-disabled") !== "true") {
            nx.setAttribute("aria-disabled", "true");
            Promise.resolve(this.host.setNext(id, this.host.flagOf(id) !== 1)).finally(() => {
              nx.removeAttribute("aria-disabled");
            });
          }
        }
      });
      next.addEventListener("click", (e) => {
        const x = e.target.closest("[data-s2-next-off]");
        if (!x || x.disabled) return;
        e.preventDefault();
        x.disabled = true;
        Promise.resolve(this.host.setNext(x.dataset.s2NextOff, false)).finally(() => {
          x.disabled = false;
        });
      });
    }
    /* ── state ── */
    setView(patch) {
      const before = JSON.stringify(this.view);
      if (patch.q !== void 0) this.view.q = String(patch.q).slice(0, 80);
      if (patch.craft !== void 0 && CRAFTS.includes(patch.craft)) this.view.craft = patch.craft;
      if (patch.sort !== void 0 && SORTS.includes(patch.sort)) this.view.sort = patch.sort;
      if (JSON.stringify(this.view) === before) return;
      this.selected.clear();
      this.host.viewChanged?.();
      this.host.repaint();
    }
    setSelecting(on) {
      this.selecting = !!on && this.editable();
      this.selected.clear();
      if (this.selStatus) this.selStatus.hidden = true;
      this.selectBtn.setAttribute("aria-pressed", String(this.selecting));
      this.paintSelect();
      this.paintBar();
      this.host.repaint();
    }
    toggleSelected(id) {
      if (id === void 0 || !this.selecting) return;
      if (this.selected.has(id)) this.selected.delete(id);
      else this.selected.add(id);
      const li = this.doc.querySelector('#pw-wishlist-grid [data-s2p="' + CSS.escape(id) + '"]');
      if (li) this.paintPick(li, id);
      this.paintBar();
    }
    async bulk(kind) {
      const ids2 = [...this.selected];
      if (kind === "done") {
        this.setSelecting(false);
        return;
      }
      if (kind === "all") {
        const all = this.last.list;
        if (ids2.length && ids2.length === all.length) this.selected.clear();
        else this.selected = new Set(all);
        this.host.repaint();
        return;
      }
      if (!ids2.length) return;
      for (const b of [this.selNext, this.selNextOff, this.selRemove]) b.disabled = true;
      this.selStatus.hidden = true;
      let ok = true;
      try {
        if (kind === "next") {
          await this.host.prepare?.(ids2);
          ok = await this.host.bulkNext(ids2.filter((id) => this.host.canNext(id)), true);
        } else if (kind === "next-off") ok = await this.host.bulkNext(ids2, false);
        else if (kind === "remove") ok = await this.host.bulkRemove(ids2);
        if (ok === false) {
          this.selStatus.textContent = this.tr("bulk_failed");
          this.selStatus.hidden = false;
        } else {
          this.selecting = false;
          this.selected.clear();
          this.selectBtn.setAttribute("aria-pressed", "false");
          this.paintSelect();
        }
      } finally {
        await this.host.repaint();
      }
      if (ok === false) return;
      if (kind === "remove") (this.undoBox.querySelector('[data-s2-undo="undo"]') || this.selectBtn).focus();
      else this.focusAfterUndo();
    }
    focusAfterUndo() {
      if (this.tools && !this.tools.hidden && !this.selectBtn.disabled) this.selectBtn.focus();
      else this.doc.getElementById("pw-wishlist-empty")?.querySelector("a,button")?.focus();
    }
    setOrdered(state2) {
      if (this.orderedState === state2) return;
      this.orderedState = state2;
      this.paintOrdered();
    }
    paintOrdered() {
      const n = this.orderedNote;
      if (!n) return;
      const st = this.ordered ? this.orderedState : null, d = this.doc;
      if (st === "checking") {
        n.replaceChildren(el(d, "span", {}, this.tr("ordered_checking")));
        n.hidden = false;
      } else if (st === "incomplete") {
        n.replaceChildren(el(d, "span", {}, this.tr("ordered_incomplete")), el(d, "button", { type: "button", class: "pw-s2-link", "data-s2-ordered-retry": "" }, this.tr("ordered_retry")));
        n.hidden = false;
      } else {
        n.hidden = true;
        n.replaceChildren();
      }
    }
    /* ── list ── */
    apply(ids2) {
      const v = viewList(ids2, this.view, (id) => this.host.metaOf(id));
      this.last = v;
      if (this.selecting) this.selected = pruneSelection(this.selected, v.list);
      if (v.waiting && isActive(this.view)) this.host.ensureMeta(ids2.filter((id) => !this.host.metaOf(id)), "view");
      return v;
    }
    active() {
      return isActive(this.view);
    }
    /* ── one card ── */
    decorate(li, id) {
      const d = this.doc, tr2 = this.tr, meta = this.host.metaOf(id), title = meta?.title || tr2("saved_design");
      li.dataset.s2p = id;
      const cta = li.querySelector(":scope > .pw-item__cta");
      let row = li.querySelector(":scope > .pw-s2-row");
      if (!row) {
        row = el(d, "div", { class: "pw-s2-row" });
        if (cta) cta.after(row);
        else li.append(row);
      }
      const craft = li.querySelector(".pkt-craft"), ov = li.querySelector(".pkt-ov");
      if (craft && ov && craft.parentNode !== ov) ov.prepend(craft);
      const flag = this.host.flagOf(id) === 1, can = this.host.canNext(id);
      let nx = li.querySelector(":scope > [data-s2-next]");
      if (!nx) nx = el(d, "button", { type: "button", class: "pw-s2-nx", "data-s2-next": "" });
      if (li.firstElementChild !== nx) li.prepend(nx);
      const heart = li.querySelector(":scope > [data-wishlist-btn]");
      if (heart && nx.nextElementSibling !== heart) nx.after(heart);
      nx.hidden = !flag && !can;
      if (nx.dataset.state !== String(flag)) {
        nx.dataset.state = String(flag);
        nx.innerHTML = (flag ? SVG.check : SVG.plus) + "<span></span>";
        nx.lastChild.textContent = flag ? tr2("next_up_added") : tr2("next_up_add");
      }
      nx.setAttribute("aria-pressed", String(flag));
      nx.setAttribute("aria-label", fill(tr2(flag ? "next_up_remove_label" : "next_up_add_label"), { title }));
      const date = this.ordered ? this.host.orderedOf(id) : void 0;
      let ord = row.querySelector(".pw-s2-ordered");
      if (date) {
        if (!ord) {
          ord = el(d, "span", { class: "pw-s2-ordered" });
          ord.innerHTML = SVG.check + "<span></span>";
          row.append(ord);
        }
        setText(ord.lastChild, orderedText(date, { locale: this.locale, template: tr2("ordered_on"), fallback: tr2("ordered") }));
      } else ord?.remove();
      li.classList.toggle("pw-s2-in-next", flag);
      this.paintPick(li, id);
    }
    paintPick(li, id) {
      let pick = li.querySelector(":scope > .pw-s2-pick");
      li.classList.toggle("pw-s2-selectable", this.selecting);
      const cta = li.querySelector(":scope > .pw-item__cta");
      if (cta) {
        if (this.selecting) {
          cta.setAttribute("tabindex", "-1");
          cta.setAttribute("aria-hidden", "true");
        } else {
          cta.removeAttribute("tabindex");
          cta.removeAttribute("aria-hidden");
        }
      }
      if (!this.selecting) {
        pick?.remove();
        li.classList.remove("pw-s2-picked");
        return;
      }
      if (!pick) {
        pick = el(this.doc, "button", { type: "button", class: "pw-s2-pick" });
        li.append(pick);
      }
      const on = this.selected.has(id), title = this.host.metaOf(id)?.title || this.tr("saved_design");
      pick.setAttribute("aria-pressed", String(on));
      pick.setAttribute("aria-label", fill(this.tr("select_item"), { title }));
      li.classList.toggle("pw-s2-picked", on);
    }
    /* ── after each grid render ── */
    after({ ids: ids2, list, limit }) {
      if (!this.mounted) return;
      const tr2 = this.tr, total = ids2.length;
      this.tools.hidden = total === 0;
      const editable = this.editable();
      if (!editable && this.selecting) {
        this.selecting = false;
        this.selected.clear();
      }
      this.q.disabled = this.sort.disabled = total === 0;
      this.selectBtn.disabled = total === 0 || !editable;
      for (const b of this.chips.querySelectorAll("[data-s2-craft]")) b.setAttribute("aria-pressed", String(b.dataset.s2Craft === this.view.craft));
      if (this.sort.value !== this.view.sort) this.sort.value = this.view.sort;
      if (this.q.value !== this.view.q && this.doc.activeElement !== this.q) this.q.value = this.view.q;
      const filtered = isFiltered(this.view);
      setText(this.resultText, filtered ? fill(tr2("results_some"), { shown: list.length, count: total }) : countText(tr2, "results_all", total));
      this.clear.hidden = !filtered;
      const { waiting, unchecked } = this.last;
      const notes = [];
      if (waiting && isActive(this.view)) notes.push(countText(tr2, "checking", waiting));
      if (unchecked && filtered) notes.push(countText(tr2, "unchecked", unchecked));
      setText(this.note, notes.join(" "));
      this.note.hidden = !notes.length;
      this.noMatch.hidden = !(total > 0 && list.length === 0 && !waiting);
      this.paintBar();
      this.paintUndo();
      this.paintOrdered();
      this.paintNext(ids2);
      this.selectBtn.setAttribute("aria-pressed", String(this.selecting));
      this.paintSelect();
    }
    /* Select kits ⇄ Done: the icon and label are rewritten only when the state changes (see setText). */
    paintSelect() {
      const on = !!this.selecting, b = this.selectBtn;
      if (!b || b.dataset.state === String(on)) return;
      b.dataset.state = String(on);
      b.innerHTML = (on ? SVG.check : SVG.box) + "<span></span>";
      b.lastChild.textContent = on ? this.tr("select_done") : this.tr("select");
    }
    editable() {
      return this.host.canEdit ? this.host.canEdit() !== false : true;
    }
    paintBar() {
      if (!this.mounted) return;
      const tr2 = this.tr, n = this.selected.size, all = this.last.list.length;
      this.selbar.hidden = !this.selecting;
      if (this.howto) this.howto.hidden = !this.selecting;
      setText(this.selCount, n ? fill(tr2("selected_count"), { count: n }) : tr2("select_prompt"));
      setText(this.selAll, n && n === all ? tr2("select_none") : fill(tr2("select_all"), { count: all }));
      this.selAll.disabled = !all;
      const ids2 = [...this.selected], anyOff = ids2.some((id) => this.host.flagOf(id) !== 1 && this.host.canNext(id)), anyOn = ids2.some((id) => this.host.flagOf(id) === 1);
      this.selNext.disabled = !anyOff;
      this.selNextOff.disabled = !anyOn;
      this.selNextOff.hidden = !anyOn;
      this.selRemove.disabled = !n;
    }
    paintUndo() {
      const st = this.host.undoState?.();
      const box = this.undoBox;
      if (!st) {
        box.hidden = true;
        box.replaceChildren();
        delete box.dataset.key;
        return;
      }
      box.hidden = false;
      const key2 = JSON.stringify([st.count, !!st.busy, !!st.failed]);
      if (box.dataset.key === key2) return;
      box.dataset.key = key2;
      const msg = st.busy ? fill(this.tr("bulk_restoring"), { count: st.count }) : countText(this.tr, "bulk_removed", st.count);
      const span = el(this.doc, "span", {}, msg);
      box.replaceChildren(span);
      if (!st.busy) box.append(el(this.doc, "button", { type: "button", class: "pw-s2-btn", "data-s2-undo": "undo" }, this.tr("undo")), el(this.doc, "button", { type: "button", class: "pw-s2-btn pw-s2-btn--quiet", "data-s2-undo": "dismiss", "aria-label": this.tr("close_undo") }, "\xD7"));
      if (st.failed) box.append(el(this.doc, "span", { class: "pw-s2-undo__fail" }, this.tr("bulk_failed")));
    }
    paintNext(ids2) {
      const d = this.doc, tr2 = this.tr, box = this.nextBox;
      if (!box) return;
      if (!ids2.length) {
        box.hidden = true;
        return;
      }
      const picked = nextUpIds(ids2, (id) => this.host.flagOf(id));
      const missing = picked.filter((id) => !this.host.metaOf(id));
      if (missing.length) this.host.ensureMeta(missing, "next");
      const editable = this.editable();
      const key2 = JSON.stringify([editable, picked.map((id) => [id, this.host.metaOf(id)?.title || this.host.metaOf(id)?.status || ""])]);
      box.hidden = false;
      if (box.dataset.key === key2) return;
      box.dataset.key = key2;
      const head = el(d, "div", { class: "pw-s2-head" });
      head.append(el(d, "span", { class: "pw-s2-head__dot", "aria-hidden": "true" }), el(d, "h2", { class: "pw-s2-head__label", id: "pw-s2-next-title" }, tr2("next_up")), el(d, "span", { class: "pw-s2-head__count" }, String(picked.length)), el(d, "i", { class: "pw-s2-head__rule", "aria-hidden": "true" }));
      const intro = el(d, "p", { class: "pw-s2-next__intro" }, tr2("next_up_intro"));
      const parts = [head, intro];
      if (!picked.length) parts.push(el(d, "p", { class: "pw-s2-next__empty" }, tr2("next_up_empty")));
      else {
        const ul = el(d, "ul", { class: "pw-s2-next__list", role: "list" });
        for (const id of picked) {
          const meta = this.host.metaOf(id), title = cardTitle(meta?.title || tr2("saved_design")), url = this.host.urlOf(id);
          const li = el(d, "li", { class: "pw-s2-next__item", "data-s2-next-item": id });
          const thumb = el(d, url ? "a" : "span", { class: "pw-s2-next__thumb", href: url || void 0, tabindex: url ? "-1" : void 0, "aria-hidden": "true" });
          if (meta?.image) thumb.append(el(d, "img", { src: meta.image, alt: "", loading: "lazy", width: "96", height: "96" }));
          const name = el(d, url ? "a" : "span", { class: "pw-s2-next__title", href: url || void 0 }, title);
          const txt2 = el(d, "div", { class: "pw-s2-next__txt" }), craftKey = { pbn: "filter_pbn", dp: "filter_dp" }[craftOf(meta)];
          if (craftKey) txt2.append(el(d, "p", { class: "pw-s2-next__craft" }, tr2(craftKey)));
          txt2.append(name);
          li.append(thumb, txt2);
          if (meta && typeof meta.title !== "string" && meta.status) li.append(el(d, "span", { class: "pw-s2-next__status" }, tr2("unavailable")));
          else if (url && meta) {
            const go = el(d, "a", { class: "pw-s2-next__cta", href: url });
            go.innerHTML = "<span></span>" + SVG.right;
            go.firstChild.textContent = tr2("choose_options");
            li.append(go);
          }
          if (editable) li.append(el(d, "button", { type: "button", class: "pw-s2-next__x", "data-s2-next-off": id, "aria-label": fill(tr2("next_up_remove_label"), { title }), html: SVG.x }));
          ul.append(li);
        }
        parts.push(ul);
      }
      box.replaceChildren(...parts);
    }
    reset() {
      this.selected.clear();
      this.selecting = false;
      if (this.mounted) {
        this.tools.hidden = true;
        this.selbar.hidden = true;
        this.nextBox.hidden = true;
        this.nextBox.replaceChildren();
        delete this.nextBox.dataset.key;
        this.undoBox.hidden = true;
        this.undoBox.replaceChildren();
        delete this.undoBox.dataset.key;
        this.selStatus.hidden = true;
        this.orderedState = null;
        this.paintOrdered();
      }
    }
  };

  // browser/step2-guest.mjs
  function createGuestStep2({ cfg: cfg2, device: device2, controller: controller2, cards: cards2, details, mapped, ids: ids2, data, run, repaint: repaint2, onViewChange = () => {
  }, doc = globalThis.document }) {
    const tr2 = step2Text(cfg2.text || {}, doc);
    let generation = 0, loading = null, bulk = null;
    const attempt = (fn) => {
      let ok = false;
      return Promise.resolve(run(async () => {
        await fn();
        ok = true;
      })).then(() => ok);
    };
    const queue = /* @__PURE__ */ new Set();
    const entry = (id) => data().guest[id];
    const host = {
      ids: ids2,
      metaOf(id) {
        const d = details.get(id);
        if (!d) return null;
        return d.meta || { status: d.status || "unknown" };
      },
      flagOf: (id) => entry(id)?.f === 1 ? 1 : 0,
      canNext(id) {
        if (!entry(id)) return false;
        const d = details.get(id);
        return !d || !!d.meta;
      },
      canEdit: () => true,
      orderedOf: () => void 0,
      urlOf(id) {
        const m = details.get(id)?.meta;
        return m?.url ? new URL(m.url, location.origin).pathname : void 0;
      },
      setNext: (id, on) => run(async () => {
        await device2.guestFlag(id, on);
      }),
      bulkNext: (list, on) => attempt(async () => {
        await device2.guestFlagMany(list.filter((id) => entry(id)), on);
      }),
      bulkRemove: (list) => attempt(async () => {
        const { removed } = await device2.guestRemoveMany(list.filter((id) => entry(id)));
        if (removed.length) bulk = { entries: removed, busy: false, failed: false };
      }),
      undoState() {
        if (!bulk) return null;
        const missing = bulk.entries.filter(([p]) => !entry(p));
        if (!missing.length && !bulk.busy) {
          bulk = null;
          return null;
        }
        return { count: bulk.busy ? bulk.entries.length : missing.length, busy: bulk.busy, failed: bulk.failed };
      },
      undo() {
        const u = bulk;
        if (!u || u.busy) return Promise.resolve(false);
        u.busy = true;
        return attempt(async () => {
          await device2.guestRestore(u.entries);
          if (bulk === u) bulk = null;
        }).then((ok) => {
          if (!ok && bulk === u) {
            u.busy = false;
            u.failed = true;
          }
          return ok;
        });
      },
      dismissUndo() {
        bulk = null;
        return repaint2();
      },
      repaint: () => repaint2(),
      viewChanged() {
        onViewChange();
      },
      ensureMeta(list) {
        for (const id of list) if (!details.has(id)) queue.add(id);
        pump();
      },
      async prepare(list) {
        const gen = generation;
        for (const id of list) if (!details.has(id)) queue.add(id);
        for (let i = 0; i < 100 && gen === generation && (queue.size || loading); i++) await pump();
      }
    };
    const ui = new WishlistStep2({ tr: tr2, host, doc, locale: cfg2.locale || doc.documentElement.lang || "en", ordered: false });
    async function pump() {
      if (loading) return loading;
      const gen = generation;
      loading = (async () => {
        while (queue.size && gen === generation) {
          const wanted = [...queue].filter((id) => entry(id) && !details.has(id));
          queue.clear();
          const hs = wanted.filter((p) => p.startsWith("h:")), nums = wanted.filter((p) => !p.startsWith("h:"));
          for (let i = 0; i < hs.length && gen === generation; i += 48) {
            const part = hs.slice(i, i + 48), byHandle = /* @__PURE__ */ new Map();
            try {
              const rows = await controller2.resolve(part.map((p) => p.slice(2)), "handles");
              for (const r of rows) {
                const p = "h:" + r.input;
                if (["listed", "unlisted"].includes(r.state) && Number.isSafeInteger(r.id)) byHandle.set(String(r.id), p);
                else details.set(p, { status: r.state || "unknown" });
              }
              const found = await cards2.batch([...byHandle.keys()].map(Number));
              for (const [id, p] of byHandle) {
                const v = found.get(id) || { status: "unknown" };
                details.set(p, v);
                if (v.meta) mapped.set(p, v.meta.handle);
              }
            } catch {
              for (const p of part) if (!details.has(p)) details.set(p, { status: "unknown" });
            }
            if (gen !== generation) return;
            await repaint2();
          }
          for (let i = 0; i < nums.length && gen === generation; i += 24) {
            const part = nums.slice(i, i + 24);
            let found;
            try {
              found = await cards2.batch(part.map(Number));
            } catch {
              found = /* @__PURE__ */ new Map();
            }
            for (const p of part) {
              const v = found.get(p) || { status: "unknown" };
              details.set(p, v);
              if (v.meta) mapped.set(p, v.meta.handle);
            }
            if (gen !== generation) return;
            await repaint2();
          }
        }
      })().finally(() => {
        loading = null;
      });
      return loading;
    }
    return {
      ui,
      tr: tr2,
      view(all) {
        ui.mount();
        return ui.apply(all);
      },
      decorate(li, id) {
        ui.decorate(li, id);
      },
      after(all, list) {
        ui.after({ ids: all, list });
      },
      reset() {
        generation++;
        queue.clear();
        bulk = null;
        ui.reset();
      }
    };
  }

  // browser/heart-label.mjs
  var HEART_TEXT = {
    heart_add_label: "Add {title} to your wishlist",
    heart_remove_label: "Remove {title} from your wishlist",
    heart_add: "Add to wishlist",
    heart_remove: "Remove from wishlist"
  };
  var TITLE = ".pkt-ttl, .bsc__ttl, .card__heading";
  var clean = (s) => String(s ?? "").replace(/\s+/g, " ").trim().slice(0, 150);
  var productHandle = (href, base) => {
    try {
      const p = new URL(href, base).pathname.replace(/\/+$/, "");
      return p.includes("/products/") ? p.split("/").pop() : "";
    } catch {
      return "";
    }
  };
  function heartTitle(b) {
    const h = b?.dataset?.productHandle, doc = b?.ownerDocument;
    if (!h || !doc) return "";
    const base = doc.defaultView?.location?.href || "https://paintlykits.com/";
    for (let n = b.parentElement, i = 0; n && n !== doc.body && i < 5; n = n.parentElement, i++) {
      if (n.querySelectorAll("[data-wishlist-btn]").length > 1) break;
      for (const a of n.querySelectorAll('a[href*="/products/"]')) {
        if (productHandle(a.getAttribute("href"), base) !== h) continue;
        const t = clean(a.querySelector(TITLE)?.textContent) || clean(a.getAttribute("aria-label")) || clean(a.closest(TITLE)?.textContent);
        if (t) return t;
      }
    }
    if (productHandle(base, base) === h) return clean(doc.querySelector("main h1")?.textContent);
    return "";
  }
  function heartLabel(b, on, text = {}) {
    const t = (k) => text[k] || HEART_TEXT[k], title = heartTitle(b);
    return title ? t(on ? "heart_remove_label" : "heart_add_label").replace("{title}", () => title) : t(on ? "heart_remove" : "heart_add");
  }
  function labelHeart(b, on, text) {
    const v = heartLabel(b, on, text);
    if (b.getAttribute("aria-label") !== v) b.setAttribute("aria-label", v);
  }

  // browser/focus-keep.mjs
  var ROLES = ["[data-s2-next]", "[data-wishlist-btn]", ".pkt-ec", ".pw-item__cta", ".pw-s2-pick"];
  function holdFocus(doc, grid) {
    const f = doc?.activeElement, li = f?.closest?.("li[data-s2p]");
    const role = li && grid?.contains(li) ? ROLES.find((s) => f.matches(s)) : null;
    if (!role) return () => {
    };
    const id = li.dataset.s2p;
    return () => {
      const now = doc.activeElement;
      if (now && now !== doc.body && now !== doc.documentElement) return;
      for (const x of grid.children) {
        if (x.dataset?.s2p !== id) continue;
        const t = x.querySelector(role);
        if (t && !t.hidden && !t.disabled) t.focus({ preventScroll: true });
        return;
      }
    };
  }

  // browser/guest.mjs
  async function installGuest({ cfg: cfg2, controller: controller2, device: device2 }) {
    const text = cfg2.text || {}, tr2 = (k) => text[k] || k, cards2 = new Cards({ controller: controller2, root: cfg2.root || "/", country: cfg2.country || "US" });
    let data = await device2.guestAdopt(), blocked = false, limit = 24, serial = Promise.resolve();
    const details = /* @__PURE__ */ new Map(), mapped = /* @__PURE__ */ new Map();
    const ids2 = () => Object.keys(data.guest).sort((a, b) => data.guest[b].g - data.guest[a].g), handles2 = () => ids2().map((p) => p.startsWith("h:") ? p.slice(2) : mapped.get(p)).filter(Boolean);
    let marketTimer = null;
    async function syncMarketing() {
      if (!cfg2.branchB || blocked) return;
      try {
        const bound = () => !guestMaySend(localStorage), identity = () => cookieIdentity(document.cookie), market = { country: cfg2.country, currency: cfg2.currency, locale: cfg2.locale }, p = await prepareGuestPayload({ read: () => device2.read(), identity, market, bound, loadFresh: async (keys) => {
          const hs = keys.filter((p2) => p2.startsWith("h:")).map((p2) => p2.slice(2)), rs = hs.length ? await controller2.resolve(hs, "handles") : [], ids3 = rs.filter((r) => ["listed", "unlisted"].includes(r.state)).map((r) => r.id);
          const fresh = new Cards({ controller: controller2, root: cfg2.root || "/", country: cfg2.country || "US" });
          return [...(await fresh.batch(ids3)).values()].map((v) => v.meta).filter(Boolean).map((x) => ({ ...x, compareAt: x.compare || 0, url: new URL(x.url, location.origin).href }));
        } });
        if (!p || bound() || blocked || !await check()) return;
        const marker = JSON.stringify([p.identity, p.marker]);
        if (localStorage.getItem("pk_wl2:kl:guest") === marker) return;
        const current = await device2.read();
        if (current.rev !== p.rev || await identity() !== p.identity || typeof window.klaviyo?.identify !== "function") return;
        await marketingSDK.call("identify", p.payload);
        localStorage.setItem("pk_wl2:kl:guest", marker);
      } catch {
      }
    }
    function status2() {
      const el2 = document.querySelector("#pw-where-text");
      if (el2) el2.textContent = device2.volatile ? tr2("guest_memory") : tr2("guest");
    }
    function sync2() {
      for (const b of document.querySelectorAll("[data-wishlist-btn]")) {
        const on = handles2().includes(b.dataset.productHandle);
        b.disabled = blocked;
        b.setAttribute("aria-pressed", String(on));
        labelHeart(b, on, text);
        b.classList.toggle("pw-heart--active", on);
      }
      for (const el2 of document.querySelectorAll("[data-wishlist-count]")) {
        el2.textContent = ids2().length;
        el2.closest(".pw-header-badge")?.classList.toggle("pw-header-badge--hidden", ids2().length === 0);
      }
      document.dispatchEvent(new CustomEvent("wishlist:changed", { detail: { v2: true, count: ids2().length, list: handles2() } }));
    }
    async function render2() {
      if (blocked) return;
      const grid = document.querySelector("#pw-wishlist-grid");
      if (!grid) {
        sync2();
        return;
      }
      const all = ids2(), view = step22.view(all), wanted = view.list.slice(0, limit), need = wanted.filter((p) => !details.has(p)), hs = need.filter((p) => p.startsWith("h:"));
      if (hs.length) {
        try {
          const rows = await controller2.resolve(hs.map((p) => p.slice(2)), "handles");
          for (const r of rows) if (["listed", "unlisted"].includes(r.state)) {
            const p = "h:" + r.input;
            details.set(p, await cards2.one(r.id, r.handle));
          } else details.set("h:" + r.input, { status: r.state });
        } catch {
        }
      }
      const nums = need.filter((p) => !p.startsWith("h:")).map(Number);
      try {
        for (const [p, v] of await cards2.batch(nums)) {
          details.set(p, v);
          if (v.meta) mapped.set(p, v.meta.handle);
        }
      } catch {
      }
      if (blocked) return;
      const keep = holdFocus(document, grid);
      grid.replaceChildren();
      for (const p of wanted) {
        const v = details.get(p);
        let li = v?.node?.cloneNode(true);
        if (!li) {
          li = document.createElement("li");
          li.className = "pw-item pw-v2-unavailable";
          const title = document.createElement("h3");
          title.textContent = tr2("saved_design");
          const note = document.createElement("p");
          note.textContent = tr2(["missing", "unavailable"].includes(v?.status) ? "unavailable" : v?.status === "not_in_market" ? "market" : "retry");
          li.append(title, note);
          if (!["missing", "unavailable", "not_in_market"].includes(v?.status)) {
            const retry = document.createElement("button");
            retry.type = "button";
            retry.textContent = tr2("try_again");
            retry.onclick = () => {
              details.delete(p);
              render2();
            };
            li.append(retry);
          }
          const b = document.createElement("button");
          b.type = "button";
          b.textContent = tr2("remove");
          b.onclick = () => act("remove", p);
          li.append(b);
        }
        step22.decorate(li, p);
        grid.append(li);
      }
      keep();
      grid.hidden = !wanted.length;
      document.querySelector("#pw-wishlist-loading")?.setAttribute("hidden", "");
      const empty = document.querySelector("#pw-wishlist-empty");
      if (empty) empty.hidden = !!ids2().length;
      const count = document.querySelector("#pw-count");
      if (count) {
        count.textContent = ids2().length;
        count.hidden = !ids2().length;
      }
      document.querySelector(".pw-sechead")?.toggleAttribute("hidden", !ids2().length);
      let more = document.getElementById("pw-v2-more");
      if (!more) {
        more = document.createElement("button");
        more.id = "pw-v2-more";
        more.textContent = tr2("more");
        more.onclick = () => {
          limit += 24;
          render2();
        };
        grid.after(more);
      }
      more.hidden = view.list.length <= limit;
      step22.after(all, view.list);
      status2();
      sync2();
      clearTimeout(marketTimer);
      if (cfg2.branchB) marketTimer = setTimeout(syncMarketing, 700);
    }
    async function check() {
      const who = await controller2.whoami();
      if (!who.guest) {
        blocked = true;
        data = { guest: {} };
        step22.reset();
        document.querySelector("#pw-wishlist-grid")?.replaceChildren();
        sync2();
        const el2 = document.querySelector("#pw-where-text");
        if (el2) el2.textContent = tr2("session");
      }
      return !blocked;
    }
    function act(t, p) {
      serial = serial.then(async () => {
        if (!await check()) return;
        data = await device2.guestAction(t, p);
        await render2();
      }).catch(() => {
        const el2 = document.querySelector("#pw-where-text");
        if (el2) el2.textContent = tr2("not_saved");
      });
      return serial;
    }
    function run(fn) {
      serial = serial.then(async () => {
        if (!await check()) return;
        await fn();
        data = await device2.read();
        await render2();
      }).catch(() => {
        const el2 = document.querySelector("#pw-where-text");
        if (el2) el2.textContent = tr2("not_saved");
      });
      return serial;
    }
    const step22 = createGuestStep2({ cfg: cfg2, device: device2, controller: controller2, cards: cards2, details, mapped, ids: ids2, data: () => data, run, repaint: () => {
      if (!blocked) return render2();
    }, onViewChange: () => {
      limit = 24;
    } });
    function toggle2(h) {
      const p = Object.keys(data.guest).find((p2) => p2 === "h:" + h || mapped.get(p2) === h);
      if (p) return act("remove", p);
      return act("save", "h:" + h);
    }
    window.PaintlyWishlist = { v2: true, isolated: true, ready: true, count: () => ids2().length, getAll: handles2, has: (h) => handles2().includes(h), toggle: toggle2, remove: (h) => {
      const p = ids2().find((p2) => p2 === "h:" + h || mapped.get(p2) === h);
      if (p) return act("remove", p);
    }, prune: () => {
    }, snapshot: () => ({ ready: true, blocked, count: ids2().length, items: [] }) };
    document.addEventListener("click", (e) => {
      const b = e.target.closest("[data-wishlist-btn]");
      if (!b) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      toggle2(b.dataset.productHandle);
    }, true);
    window.addEventListener("focus", () => check().catch(() => {
    }));
    await render2();
  }

  // src/transport.mjs
  var ScopeError = class extends Error {
    constructor() {
      super("scope_mismatch");
      this.code = "scope_mismatch";
    }
  };
  var ControlError = class extends Error {
    constructor(code) {
      super(code);
      this.code = code;
    }
  };
  function createTransport({ scope, fetcher = fetch, base = "/apps/paintly/v2", timers = globalThis, onScopeMismatch = () => {
  } }) {
    if (!/^\/apps\/paintly\/v2\/?$/.test(base)) throw Error("same_origin_proxy_required");
    const mismatch = () => {
      onScopeMismatch();
      throw new ScopeError();
    };
    async function call(name, wire) {
      const get = name === "state" || name === "whoami";
      const payload = { ...wire, scope };
      const url = base.replace(/\/$/, "") + "/" + name + (name === "state" ? "?scope=" + encodeURIComponent(JSON.stringify(scope)) : "");
      const controller2 = new AbortController();
      let timer2;
      const expired = new Promise((_, reject) => {
        timer2 = timers.setTimeout(() => {
          controller2.abort();
          reject(new Error("transport_timeout"));
        }, 45e3);
      });
      try {
        return await Promise.race([(async () => {
          const r = await fetcher(url, { method: get ? "GET" : "POST", credentials: "same-origin", cache: "no-store", headers: { "Content-Type": "application/json" }, ...!get ? { body: JSON.stringify(payload) } : {}, signal: controller2.signal });
          if (r.status === 401) return mismatch();
          const body = await r.json();
          if (body.error === "scope_mismatch") return mismatch();
          const retryAfter = Number(r.headers.get("Retry-After"));
          if (r.status === 503 || r.status === 429) {
            const code = String(r.status);
            if (name === "state" || name === "whoami") throw new Error("transport_" + code);
            return name === "ops" ? { batch: code, retryAfter: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : code === "429" ? 60 : 3 } : { error: code };
          }
          const error = body.v2 === false ? "legacy" : body.error === "authority_changed" ? "legacy" : body.error;
          if (error) {
            if (name === "ops") return { batch: error, ...body.inc ? { inc: body.inc } : {}, ...body.expect ? { expect: body.expect } : {} };
            if (name === "register") return { error, ...body.inc ? { inc: body.inc } : {} };
            throw new ControlError(error);
          }
          if (!r.ok) throw new Error("transport_response");
          if (name === "state") {
            if (!body.state || !Number.isSafeInteger(body.state.inc) || !Number.isSafeInteger(body.state.rev) || !body.state.items || !body.state.gone) throw Error("transport_shape");
            return body.state;
          }
          if (name === "register") {
            if (body.error) return body;
            if (!Number.isSafeInteger(body.epoch) || body.epoch < 1) throw Error("transport_shape");
          }
          if (name === "ops" && !body.batch && (!Array.isArray(body.results) || !body.state)) throw Error("transport_shape");
          return body;
        })(), expired]);
      } finally {
        timers.clearTimeout(timer2);
      }
    }
    return { state: () => call("state"), register: (q) => call("register", q), ops: (q) => call("ops", q), whoami: () => call("whoami") };
  }

  // browser/controller.mjs
  var randomKey = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
  var HOLD_MS = 18e5;
  var holds = (s, now) => (Array.isArray(s.hold) ? s.hold : []).filter((h) => h && Array.isArray(h.seqs) && now - h.at < HOLD_MS);
  function optimistic(s) {
    var _a;
    const items = structuredClone(s.inst.items);
    for (const e of Object.values(s.outbox).sort((a, b) => a.op.seq - b.op.seq)) {
      const o = e.op;
      if (o.t === "save") items[_a = o.p] ?? (items[_a] = { g: o.base + 1, f: 0 });
      if (o.t === "remove") delete items[o.p];
      if (o.t === "next" && items[o.p]) items[o.p].f = +o.on;
    }
    return items;
  }
  var PageController = class {
    constructor({ fetcher = (...args) => globalThis.fetch(...args), storage, clock = { now: () => Date.now() }, expectedCustomer = null, notify = () => {
    }, readOptions = {}, locks = globalThis.navigator?.locks, tab = randomKey() }) {
      Object.assign(this, { fetcher, storage, clock, expectedCustomer, notify, readOptions, locks, tab });
      this.phase = "loading";
      this.serial = Promise.resolve();
      this.blocked = false;
      this.outbox = null;
      this.recovery = null;
    }
    async whoami() {
      return boundedRead(this.fetcher, "/apps/paintly/v2/whoami", { credentials: "same-origin", cache: "no-store" }, async (r) => {
        if (!r.ok) throw Error("identity_unavailable");
        const v = await r.json();
        if (v.guest === true) return v;
        if (!Number.isSafeInteger(v.cid) || v.cid <= 0 || typeof v.sid !== "string" || v.sid.length < 20) throw Error("identity_invalid");
        return v;
      }, this.readOptions);
    }
    async suspend() {
      this.blocked = true;
      this.phase = "signed_out";
      this.notify();
      if (this.storage && this.outbox) {
        const o = this.outbox;
        await this.storage.transact((root) => {
          if (root.active.scope === o.scope && root.active.generation === o.identity) root.active = { scope: null, generation: root.active.generation + 1 };
        });
      }
    }
    stageAt(stage) {
      this.stage = stage;
      this.notify();
    }
    async start() {
      this.stageAt("identity");
      const who = await this.whoami();
      if (who.guest) {
        if (this.expectedCustomer) {
          await this.suspend();
          return this.phase;
        }
        this.phase = "guest";
        return this.phase;
      }
      if (this.expectedCustomer && who.cid !== this.expectedCustomer) {
        await this.suspend();
        return this.phase;
      }
      this.identity = who;
      this.scope = { cid: who.cid, sid: who.sid };
      const guarded = async (u, options) => {
        if (this.blocked) throw new ScopeError();
        const r = await this.fetcher(u, options);
        if (u.includes("/state") && r.ok) {
          try {
            const body = await r.clone().json();
            this.recovery = body.recovery ?? null;
            this.readOnly = body.readOnly === true;
            this.imports = body.imports ?? [];
          } catch {
          }
        }
        return r;
      };
      this.transport = createTransport({ scope: this.scope, fetcher: guarded, onScopeMismatch: () => {
        this.suspending = this.suspend();
      } });
      this.stageAt("account-read");
      let state2;
      try {
        state2 = await this.transport.state();
      } catch (e) {
        if (e.code === "legacy") {
          this.phase = "legacy";
          return this.phase;
        }
        throw e;
      }
      this.stageAt("device-storage");
      if (!this.storage) {
        const opened = await openStorage("paintly-wishlist-v2");
        this.storage = opened.storage;
        this.warning = opened.warning;
      }
      this.stageAt("device-open");
      this.outbox = await LocalOutbox.open({ storage: this.storage, clock: this.clock, transport: this.transport, scope: "account:" + who.cid, client: randomKey(), tab: this.tab, locks: this.locks, state: state2 });
      this.stageAt("device-install");
      await this.outbox.tx((s) => install(s, state2));
      this.phase = "account";
      this.stageAt("ready");
      this.unwatch = this.outbox.watch((s) => {
        if (s?.stale) {
          this.blocked = true;
          this.phase = "signed_out";
        }
        this.notify();
      });
      this.notify();
      return this.phase;
    }
    async inspect() {
      if (!this.outbox) return null;
      const s = await this.outbox.inspect();
      if (s.stale) {
        this.blocked = true;
        this.phase = "signed_out";
        return null;
      }
      return s;
    }
    action(t, p, options = {}) {
      const run = async () => {
        if (this.blocked || this.readOnly || this.phase !== "account") throw Error("wishlist_not_writable");
        const result = await this.outbox.append(t, p, options);
        if (result?.stale) throw Error("stale_identity");
        this.notify();
        return result;
      };
      const task = this.serial.then(run);
      this.serial = task.catch(() => {
      });
      return task;
    }
    /** Step 2 bulk actions. The whole bounded list (at most 3,000 ops: 1,500 removals plus their Next up flags)
     * is appended in ONE durable transaction, so a failure leaves nothing queued and a success leaves every op
     * queued: there is never a committed prefix without its Undo receipt. Each op still gets its own sequence,
     * dependency and base exactly as single appends would. With {hold:true} the same transaction records the
     * sequences whose results (removal witnesses) an Undo receipt needs, so sending them in several batches
     * cannot prune a witness before the page reads it; release() drops the hold. Holds expire after 30 minutes
     * and are bounded to 6,000 sequences. If the batch would pass the end of the sequence space it is refused
     * with nothing queued (a later single action rotates the client as before; MAX is 10^12). */
    actionMany(list, { hold = false } = {}) {
      const run = async () => {
        if (this.blocked || this.readOnly || this.phase !== "account") throw Error("wishlist_not_writable");
        if (!Array.isArray(list) || list.length > 3e3) throw Error("bulk_too_large");
        if (!list.length) return [];
        const now = this.clock.now();
        const result = await this.outbox.tx((s) => {
          if (s.nextSeq + list.length - 1 > MAX) return { rotate: true };
          const seqs = list.map(([t, p, options]) => append(s, t, p, options || {}));
          if (hold) {
            s.hold = [...holds(s, now), { at: now, seqs: seqs.filter(Number.isSafeInteger) }];
            while (s.hold.reduce((n, h) => n + h.seqs.length, 0) > 6e3) s.hold.shift();
          }
          return seqs;
        });
        if (result?.stale) throw Error("stale_identity");
        if (result?.rotate) throw Error("sequence_rotation_pending");
        this.notify();
        return result;
      };
      const task = this.serial.then(run);
      this.serial = task.catch(() => {
      });
      return task;
    }
    /** Drops the hold made for these sequences (and any expired hold). Never touches queued operations. */
    release(seqs) {
      if (!this.outbox) return Promise.resolve();
      const now = this.clock.now(), first = seqs?.[0];
      return this.outbox.tx((s) => {
        if (!s.hold) return;
        s.hold = holds(s, now).filter((h) => first === void 0 || h.seqs[0] !== first);
      }).catch(() => {
      });
    }
    async send() {
      if (this.blocked) return "scope_mismatch";
      if (!this.outbox || this.readOnly) return "paused";
      if (this.sending) return this.sending;
      this.sending = (async () => {
        try {
          const r = await this.outbox.drain(40);
          this.notify();
          return r;
        } catch (e) {
          if (e.code === "scope_mismatch") {
            await this.suspending;
            return "scope_mismatch";
          }
          if (e.code === "paused" || e.code === "legacy") {
            this.phase = e.code;
            this.notify();
            return e.code;
          }
          throw e;
        } finally {
          try {
            const current = await this.inspect();
            if (current && !Object.keys(current.outbox).length) await this.outbox.release();
          } finally {
            this.sending = null;
          }
        }
      })();
      return this.sending;
    }
    async resume() {
      const who = await this.whoami();
      if (who.guest || who.cid !== this.scope?.cid || who.sid !== this.scope.sid) {
        await this.suspend();
        return false;
      }
      this.blocked = false;
      this.phase = "loading";
      await this.start();
      return this.phase === "account";
    }
    async resolve(values, kind = "ids", country = "US", bypass = false) {
      if (this.blocked) throw new ScopeError();
      const out = [];
      for (let i = 0; i < values.length; i += 50) {
        const part = values.slice(i, i + 50), scope = this.scope, b = await boundedRead(this.fetcher, "/apps/paintly/v2/resolve", { method: "POST", credentials: "same-origin", cache: "no-store", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ [kind]: part, ...kind === "ids" ? { country } : {}, bypass, ...!scope ? { client: this.tab } : {} }) }, async (r) => {
          if (r.status === 401) {
            await this.suspend();
            throw new ScopeError();
          }
          if (!r.ok) {
            const e = Error("resolve_retry");
            e.retryAfter = Number(r.headers.get("Retry-After")) || 3;
            throw e;
          }
          return r.json();
        }, this.readOptions);
        if (this.blocked || scope !== this.scope) throw new ScopeError();
        if (!Array.isArray(b.results) || b.results.length !== part.length || b.results.some((x, j) => String(x.input) !== String(part[j]))) throw Error("resolve_shape");
        out.push(...b.results);
      }
      return out;
    }
    close() {
      this.unwatch?.();
      this.storage?.close();
    }
  };

  // browser/legacy-view.mjs
  function legacySnapshot(value) {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.filter((h) => typeof h === "string" && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h)))];
  }
  async function installLegacyView({ cfg: cfg2, controller: controller2, document: document2 = globalThis.document, window: window2 = globalThis.window, fetcher = (...a) => globalThis.fetch(...a) }) {
    const text = cfg2.text || {}, tr2 = (k) => text[k] || k, handles2 = legacySnapshot(cfg2.legacySnapshot), parser = new Cards({ controller: controller2 }), rows = /* @__PURE__ */ new Map(), details = /* @__PURE__ */ new Map(), failed = /* @__PURE__ */ new Set();
    let valid = true, limit = 24, busy = false;
    const el2 = (tag, value) => {
      const n = document2.createElement(tag);
      if (value) n.textContent = value;
      return n;
    };
    const get = () => valid ? handles2.slice() : [];
    const bridge = { v2: false, isolated: true, snapshot: () => ({ ready: true, blocked: !valid, readOnly: true, count: get().length, items: get().slice().reverse().slice(0, 6).map((h) => ({ id: "h:" + h, status: details.has(h) ? "loaded" : failed.has(h) ? "unknown" : "loading", meta: details.get(h) || null })) }), readOnly: true, ready: true, getAll: get, count: () => get().length, has: (h) => valid && handles2.includes(h), toggle: () => false, remove: () => false, prune: () => {
    }, whenReady: Promise.resolve() };
    window2.PaintlyWishlist = bridge;
    function sync2() {
      for (const b of document2.querySelectorAll("[data-wishlist-btn]")) {
        const on = bridge.has(b.dataset.productHandle);
        b.disabled = true;
        b.setAttribute("aria-disabled", "true");
        b.setAttribute("aria-pressed", String(on));
        labelHeart(b, on, text);
        b.classList.toggle("pw-heart--active", on);
      }
      for (const n of document2.querySelectorAll("[data-wishlist-count]")) {
        if (n.textContent !== String(get().length)) n.textContent = String(get().length);
        n.closest(".pw-header-badge")?.classList.toggle("pw-header-badge--hidden", !get().length);
      }
    }
    function publish2() {
      sync2();
      document2.dispatchEvent(new window2.CustomEvent("wishlist:changed", { detail: { list: get(), count: get().length, readOnly: true } }));
    }
    function message(key2) {
      const n = document2.querySelector("#pw-where-text");
      if (n) n.textContent = tr2(key2);
      const p = document2.querySelector("#pw-where");
      if (p) p.hidden = false;
    }
    function revoke() {
      valid = false;
      document2.querySelector("#pw-wishlist-grid")?.replaceChildren();
      document2.querySelector("#pw-v2-more")?.remove();
      const count = document2.querySelector("#pw-count");
      if (count) count.textContent = "0";
      message("session");
      publish2();
    }
    const heart = (e) => {
      if (e.target.closest?.("[data-wishlist-btn]")) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    document2.addEventListener("click", heart, true);
    const observer = new window2.MutationObserver(sync2);
    observer.observe(document2.body, { childList: true, subtree: true });
    window2.addEventListener("focus", async () => {
      try {
        const who = await controller2.whoami();
        if (who.guest || who.cid !== controller2.scope?.cid || who.sid !== controller2.scope?.sid) revoke();
      } catch {
        revoke();
      }
    });
    async function card(handle) {
      try {
        const html = await boundedRead(fetcher, (cfg2.root || "/") + "products/" + encodeURIComponent(handle) + "?section_id=pw-card", { credentials: "same-origin", cache: "no-store" }, async (r) => r.ok ? await r.text() : "");
        const doc = new window2.DOMParser().parseFromString(html, "text/html");
        let meta;
        try {
          meta = JSON.parse(doc.querySelector("script[data-pw-meta]")?.textContent || "null");
        } catch {
        }
        if (meta?.handle === handle && Number.isSafeInteger(meta.id) && meta.id > 0) {
          const v = parser.parse(html, /* @__PURE__ */ new Set([String(meta.id)])).get(String(meta.id));
          if (v?.node) {
            if (valid) details.set(handle, v.meta);
            return v.node;
          }
        }
      } catch {
      }
      if (valid) failed.add(handle);
      const n = el2("li");
      n.className = "pw-item pw-v2-unavailable";
      n.append(el2("h3", tr2("saved_design")), el2("p", tr2("retry")));
      return n;
    }
    async function render2() {
      const grid = document2.querySelector("#pw-wishlist-grid");
      const account = document2.getElementById("pa-saved");
      if (!grid && !account || busy || !valid) return;
      busy = true;
      try {
        const wanted = handles2.slice().reverse().slice(0, grid ? limit : 6);
        let cursor = 0;
        await Promise.all(Array.from({ length: Math.min(4, wanted.length) }, async () => {
          while (valid && cursor < wanted.length) {
            const h = wanted[cursor++];
            if (!rows.has(h)) {
              const n = await card(h);
              if (valid) rows.set(h, n);
            }
          }
        }));
        if (!valid) return;
        if (!grid) {
          publish2();
          return;
        }
        grid.replaceChildren(...wanted.map((h) => rows.get(h)).filter(Boolean));
        grid.hidden = !wanted.length;
        const count = document2.querySelector("#pw-count");
        if (count) {
          count.textContent = handles2.length;
          count.hidden = !handles2.length;
        }
        const empty = document2.querySelector("#pw-wishlist-empty");
        if (empty) empty.hidden = !!handles2.length;
        const loading = document2.querySelector("#pw-wishlist-loading");
        if (loading) loading.hidden = true;
        document2.querySelector(".pw-sechead")?.toggleAttribute("hidden", !handles2.length);
        let more = document2.getElementById("pw-v2-more");
        if (!more) {
          more = el2("button", tr2("more"));
          more.id = "pw-v2-more";
          more.type = "button";
          more.className = "pw-btn pw-btn--quiet";
          more.addEventListener("click", () => {
            limit += 24;
            render2();
          });
          grid.after(more);
        }
        more.hidden = handles2.length <= limit;
        sync2();
      } finally {
        busy = false;
      }
    }
    message("paused");
    publish2();
    await render2();
    return { revoke, dispose() {
      observer.disconnect();
      document2.removeEventListener("click", heart, true);
    } };
  }

  // browser/view.mjs
  function viewSnapshot({ items, details, ready, blocked = false, readOnly = false }, limit = 6) {
    if (blocked) return { ready: true, blocked: true, readOnly: true, count: 0, items: [] };
    const ids2 = Object.keys(items).sort((a, b) => items[b].g - items[a].g || String(b).localeCompare(String(a), "en", { numeric: true }));
    return { ready, blocked: false, readOnly, count: ids2.length, items: ids2.slice(0, limit).map((id) => {
      const detail = details.get(id), meta = detail?.meta;
      return { id, status: meta ? "loaded" : detail?.status ?? "loading", meta: meta ? structuredClone(meta) : null };
    }) };
  }

  // browser/step2-account.mjs
  function createAccountStep2({ cfg: cfg2, state: state2, controller: controller2, cards: cards2, action: action2, repaint: repaint2, ids: ids2, onRemoved = () => {
  }, doc = globalThis.document }) {
    const tr2 = step2Text(cfg2.text || {}, doc);
    let generation = 0, loading = null, marks = /* @__PURE__ */ new Map(), marksFor = null, bulk = null, orderedToken = null, orderedRunning = false;
    const attempt = (fn) => {
      let ok = false;
      return Promise.resolve(action2(async () => {
        await fn();
        ok = true;
      })).then(() => ok);
    };
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const seen = () => state2.last ? { base: state2.last.inst.rev, baseInc: state2.last.inc } : null;
    const loadQueue = /* @__PURE__ */ new Set();
    const host = {
      ids: ids2,
      metaOf(id) {
        const meta = state2.metas.get(id);
        if (meta) return meta;
        const d = state2.details.get(id);
        if (d && !d.meta) return { status: d.status || "unknown" };
        return null;
      },
      flagOf: (id) => state2.items[id]?.f === 1 ? 1 : 0,
      canNext(id) {
        if (String(id).startsWith("h:") || !state2.items[id] || controller2.readOnly) return false;
        const d = state2.details.get(id);
        return !d || !!d.meta;
      },
      // A brief background re-check (phase 'loading') does not end Select; only a real stop does.
      canEdit: () => !controller2.readOnly && !controller2.blocked && !["signed_out", "paused", "legacy", "guest"].includes(controller2.phase),
      orderedOf: (id) => marks.get(String(id)),
      urlOf(id) {
        const m = state2.metas.get(id);
        return m?.url ? new URL(m.url, location.origin).pathname : void 0;
      },
      setNext(id, on) {
        const s0 = seen();
        return action2(async () => {
          if (!s0) throw Error("not_ready");
          if (String(id).startsWith("h:")) return;
          const s = await controller2.inspect(), cur = s && optimistic(s)[id];
          if (!cur || cur.f === 1 === on) return;
          await controller2.action("next", Number(id), { on, base: s0.base, baseInc: s0.baseInc });
        });
      },
      bulkNext(list, on) {
        const s0 = seen();
        return attempt(async () => {
          if (!s0) throw Error("not_ready");
          const s = await controller2.inspect(), cur = s ? optimistic(s) : {};
          const ops = list.filter((id) => !String(id).startsWith("h:") && cur[id] && cur[id].f === 1 !== on).map((id) => ["next", Number(id), { on, base: s0.base, baseInc: s0.baseInc }]);
          if (ops.length) await controller2.actionMany(ops);
        });
      },
      bulkRemove(list) {
        const s0 = seen();
        return attempt(async () => {
          if (!s0) throw Error("not_ready");
          const s = await controller2.inspect(), cur = s ? optimistic(s) : {};
          const targets = list.filter((id) => cur[id]).map((id) => String(id).startsWith("h:") ? id : Number(id));
          if (!targets.length) return;
          const seqs = await controller2.actionMany(targets.map((p) => ["remove", p, { base: s0.base, baseInc: s0.baseInc }]), { hold: true });
          if (bulk) controller2.release(bulk.items.map((x) => x.seq));
          bulk = { inc: s0.baseInc, base: s0.base, items: targets.map((p, i) => ({ p, seq: seqs[i], f: cur[p]?.f === 1 ? 1 : 0 })).filter((x) => Number.isSafeInteger(x.seq)), busy: 0, failed: false };
          for (const p of targets) onRemoved(p);
        });
      },
      undoState() {
        if (!bulk || !state2.last || bulk.inc !== state2.last.inc) return null;
        const s = state2.last;
        for (const it of bulk.items) {
          const w = s.known[it.seq]?.w;
          if (w?.[0] === "r") it.r = w[1];
        }
        const live = bulk.items.filter((it) => s.outbox[it.seq] || it.r !== void 0 && !s.inst.items[it.p] && s.inst.gone[it.p]?.rm === it.r);
        if (!live.length && !bulk.busy) return null;
        return { count: bulk.busy ? bulk.busy : live.length, busy: !!bulk.busy, failed: bulk.failed };
      },
      undo() {
        const u = bulk;
        if (!u || u.busy) return Promise.resolve(false);
        u.busy = u.items.length;
        return attempt(async () => {
          const s = await controller2.inspect();
          if (!s || s.inc !== u.inc) {
            if (bulk === u) bulk = null;
            throw Error("undo_identity_changed");
          }
          const ops = [];
          for (const it of u.items) {
            const w = s.known[it.seq]?.w;
            if (w?.[0] === "r") it.r = w[1];
            const pending2 = s.outbox[it.seq];
            if (!pending2 && (it.r === void 0 || s.inst.items[it.p] || s.inst.gone[it.p]?.rm !== it.r)) continue;
            ops.push(["save", it.p, { base: u.base, baseInc: u.inc, ...pending2 ? {} : { r: it.r } }]);
            if (it.f === 1 && !String(it.p).startsWith("h:")) ops.push(["next", it.p, { on: true, base: u.base, baseInc: u.inc }]);
          }
          if (ops.length) await controller2.actionMany(ops);
          if (bulk === u) bulk = null;
          controller2.release(u.items.map((x) => x.seq));
        }).then((ok) => {
          if (!ok && bulk === u) {
            u.busy = 0;
            u.failed = true;
          }
          return ok;
        });
      },
      dismissUndo() {
        if (bulk) controller2.release(bulk.items.map((x) => x.seq));
        bulk = null;
        return repaint2();
      },
      retryOrdered() {
        if (!orderedRunning) return loadMarks({ resume: true });
      },
      repaint: () => repaint2(),
      viewChanged() {
        state2.limit = 24;
      },
      ensureMeta(list) {
        for (const id of list) if (!state2.details.has(id)) loadQueue.add(id);
        pump();
      },
      // Bulk Next up checks every selected design first, so unavailable ones are never put in Next up unseen.
      async prepare(list) {
        const gen = generation;
        for (const id of list) if (!state2.details.has(id)) loadQueue.add(id);
        for (let i = 0; i < 100 && gen === generation && !controller2.blocked && (loadQueue.size || loading); i++) await pump();
      }
    };
    const ui = new WishlistStep2({ tr: tr2, host, doc, locale: cfg2.locale || doc.documentElement.lang || "en", ordered: true });
    async function pump() {
      if (loading) return loading;
      const gen = generation;
      loading = (async () => {
        while (loadQueue.size && gen === generation && !controller2.blocked) {
          const wanted = [...loadQueue].filter((id) => state2.items[id] && !state2.details.has(id));
          loadQueue.clear();
          if (!wanted.length) break;
          const nums = wanted.filter((id) => !id.startsWith("h:")), hs = wanted.filter((id) => id.startsWith("h:"));
          for (let i = 0; i < nums.length && gen === generation && !controller2.blocked; i += 24) {
            const part = nums.slice(i, i + 24);
            let data;
            try {
              data = await cards2.batch(part.map(Number));
            } catch {
              data = /* @__PURE__ */ new Map();
            }
            if (gen !== generation || controller2.blocked) return;
            for (const p of part) {
              const v = data.get(p) || { status: "unknown" };
              if (state2.details.has(p)) continue;
              state2.details.set(p, v);
              if (v.meta) {
                state2.mapped.set(p, v.meta.handle);
                state2.metas.set(p, v.meta);
              }
            }
            await repaint2();
          }
          if (hs.length && gen === generation && !controller2.blocked) {
            try {
              const rows = await controller2.resolve(hs.map((p) => p.slice(2)), "handles");
              for (const r of rows) {
                const p = "h:" + r.input;
                if (["listed", "unlisted"].includes(r.state)) {
                  const v = await cards2.one(r.id, r.handle);
                  if (v?.meta) {
                    state2.details.set(p, v);
                    state2.metas.set(p, v.meta);
                  }
                } else state2.details.set(p, { status: r.state || "unknown" });
              }
            } catch {
              for (const p of hs) if (!state2.details.has(p)) state2.details.set(p, { status: "unknown" });
            }
            if (gen !== generation || controller2.blocked) return;
            await repaint2();
          }
        }
      })().finally(() => {
        loading = null;
      });
      return loading;
    }
    const ORDERED_SEGMENTS = 40, ORDERED_FAILURES = 3, ORDERED_MS = 18e4;
    async function loadMarks({ resume = false } = {}) {
      const scope = controller2.scope;
      if (!scope || controller2.blocked || orderedRunning) return;
      if (!resume && marksFor === scope) return;
      marksFor = scope;
      orderedRunning = true;
      const gen = generation, t0 = Date.now(), live = () => gen === generation && !controller2.blocked && controller2.scope === scope;
      let token = resume ? orderedToken : null, failures = 0, segments = 0, restarts = 0;
      const stop = async (st) => {
        orderedToken = st === "incomplete" ? token : null;
        ui.setOrdered(st);
        await repaint2();
      };
      try {
        if (resume) ui.setOrdered("checking");
        for (; ; ) {
          if (!live()) return;
          if (segments >= ORDERED_SEGMENTS || Date.now() - t0 > ORDERED_MS) return await stop("incomplete");
          const url = "/apps/paintly/v2/ordered?scope=" + encodeURIComponent(JSON.stringify(scope)) + (token ? "&cursor=" + encodeURIComponent(token) : "");
          let res;
          try {
            res = await boundedRead(controller2.fetcher, url, { credentials: "same-origin", cache: "no-store" }, async (r) => ({ status: r.status, retryAfter: Number(r.headers.get("Retry-After")) || 0, body: r.status === 200 || r.status === 409 ? await r.json().catch(() => null) : null }), { ms: 15e3 });
          } catch {
            res = { status: 0, retryAfter: 0 };
          }
          if (!live()) return;
          if (res.status === 404 && !token && !segments) {
            ui.setOrdered(null);
            return;
          }
          if (res.status === 409 && /^cursor_/.test(res.body?.error || "") && restarts < 2) {
            restarts++;
            token = null;
            marks = /* @__PURE__ */ new Map();
            continue;
          }
          const body = res.status === 200 ? res.body : null;
          if (!body || body.v !== 1 || !Array.isArray(body.marks)) {
            if (++failures > ORDERED_FAILURES) return await stop("incomplete");
            if (token || segments) ui.setOrdered("checking");
            await sleep(Math.min(res.status === 429 ? 6e4 : 3e4, Math.max(res.retryAfter * 1e3, 1e3 * 2 ** failures)));
            continue;
          }
          failures = 0;
          segments++;
          for (const [p, d] of parseMarks(body, ids2())) if (!marks.has(p) || marks.get(p) < d) marks.set(p, d);
          if (body.complete === true) return await stop("complete");
          if (typeof body.next !== "string" || !body.next) return await stop("incomplete");
          token = body.next;
          ui.setOrdered("checking");
          await repaint2();
          if (body.retryAfter > 0) await sleep(Math.min(3e4, body.retryAfter * 1e3));
        }
      } finally {
        if (gen === generation) orderedRunning = false;
      }
    }
    return {
      ui,
      tr: tr2,
      mount() {
        ui.mount();
      },
      view(all) {
        ui.mount();
        return ui.apply(all);
      },
      decorate(li, id) {
        ui.decorate(li, id);
      },
      after(all, list) {
        ui.after({ ids: all, list });
      },
      loadMarks,
      reset() {
        generation++;
        loadQueue.clear();
        marks = /* @__PURE__ */ new Map();
        marksFor = null;
        bulk = null;
        orderedToken = null;
        orderedRunning = false;
        ui.reset();
      }
    };
  }

  // browser/production-entry.mjs
  var s2tr = null;
  var cfg = window.PAINTLY_WL2 || {};
  var txt = cfg.text || {};
  var tr = (k) => txt[k] || (s2tr ?? (s2tr = step2Text(txt)))(k);
  var state = { items: {}, mapped: /* @__PURE__ */ new Map(), metas: /* @__PURE__ */ new Map(), details: /* @__PURE__ */ new Map(), limit: 24, rows: /* @__PURE__ */ new Map(), last: null, notice: "", actions: Promise.resolve(), paint: Promise.resolve(), stopped: false };
  var lifecycle = pageLifecycle();
  var controller = new PageController({ fetcher: lifecycle.fetch, expectedCustomer: cfg.customer || null, notify: () => scheduleRefresh() });
  var cards = new Cards({ controller, root: cfg.root || "/", country: cfg.country || "US" });
  var marketing = null;
  var saveEvents = null;
  var marketingTimer = null;
  var imports = null;
  var device = null;
  var offers = [];
  var importsLoaded = false;
  var undo = null;
  var step2 = null;
  var timer = null;
  var retryAttempt = 0;
  var refreshTimer = null;
  var questionLimit = 24;
  var noticeLimit = 24;
  var confirmations = /* @__PURE__ */ new Map();
  var seenHearts = /* @__PURE__ */ new Set();
  function scheduleRefresh() {
    if (!lifecycle.active || refreshTimer !== null) return;
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      refresh().catch(() => status(tr("retry")));
    }, 50);
  }
  function node(tag, content, cls) {
    const e = document.createElement(tag);
    if (content) e.textContent = content;
    if (cls) e.className = cls;
    return e;
  }
  function button(label, fn) {
    const b = node("button", label, "pw-btn pw-btn--quiet");
    b.type = "button";
    b.addEventListener("click", fn);
    return b;
  }
  function status(message) {
    const t = document.querySelector("#pw-where-text");
    if (t) t.textContent = message;
    const p = document.querySelector("#pw-where");
    if (p) p.hidden = false;
  }
  function ids() {
    return Object.keys(state.items).sort((a, b) => state.items[b].g - state.items[a].g || Number(b) - Number(a));
  }
  function handles() {
    return ids().map((p) => p.startsWith("h:") ? p.slice(2) : state.mapped.get(p)).filter(Boolean).reverse();
  }
  function sync() {
    document.querySelectorAll("[data-wishlist-btn]").forEach((b) => {
      const p = [...state.mapped].find(([, h]) => h === b.dataset.productHandle)?.[0], on = !!(p && state.items[p]) || !!state.items["h:" + b.dataset.productHandle];
      b.classList.toggle("pw-heart--active", on);
      b.setAttribute("aria-pressed", String(on));
      labelHeart(b, on, txt);
      b.disabled = controller.phase !== "account" || controller.readOnly || !p && !state.items["h:" + b.dataset.productHandle];
      b.setAttribute("aria-busy", String(controller.phase === "account" && !p && !state.items["h:" + b.dataset.productHandle]));
    });
    document.querySelectorAll("[data-wishlist-count]").forEach((e) => {
      if (e.textContent !== String(ids().length)) e.textContent = ids().length;
      e.closest(".pw-header-badge")?.classList.toggle("pw-header-badge--hidden", ids().length === 0);
    });
  }
  async function send() {
    clearTimeout(timer);
    if (state.stopped || !lifecycle.active || controller.blocked) return;
    let result;
    try {
      for (let i = 0; i < 40; i++) {
        const work = await imports?.pump();
        result = await controller.send();
        if (!["drained", "empty"].includes(result)) break;
        const current = await controller.inspect();
        if (!work && !Object.keys(current?.outbox || {}).length && !current?.jobs.some((j) => j.status === "running" || j.status === "finishing" || j.status === "done" && j.kind === "recovery" && j.rv && !j.ack)) break;
      }
    } catch {
      result = "uncertain";
    }
    await refresh();
    const s = await controller.inspect();
    if (s && Object.keys(s.outbox).length && !controller.blocked && !["gap", "legacy", "paused"].includes(result)) {
      retryAttempt++;
      const delay = Math.max(3e3, Math.min(6e4, 3e3 * 2 ** Math.min(retryAttempt, 4)), (s.retryAt || 0) - Date.now());
      timer = setTimeout(send, delay);
    } else retryAttempt = 0;
  }
  function action(fn) {
    state.actions = state.actions.then(fn).then(async () => {
      await refresh();
      send();
    }).catch(() => {
      state.notice = controller.blocked ? tr("session") : tr("not_saved");
      status(state.notice);
    });
    return state.actions;
  }
  async function target(handle) {
    const existing = [...state.mapped].find(([, h]) => h === handle);
    if (existing) return Number(existing[0]);
    if (state.items["h:" + handle]) return "h:" + handle;
    const r = (await controller.resolve([handle], "handles"))[0];
    if (!["listed", "unlisted"].includes(r.state)) throw Error("cannot_resolve");
    state.mapped.set(String(r.id), r.handle);
    return r.id;
  }
  function toggle(handle) {
    const seen = state.last ? { base: state.last.inst.rev, baseInc: state.last.inc } : null;
    return action(async () => {
      if (!seen) throw Error("not_ready");
      const p = await target(handle), s = await controller.inspect(), current = optimistic(s);
      const type = current[p] ? "remove" : "save";
      const seq = await controller.action(type, p, seen);
      if (type === "remove") {
        saveEvents?.cancel(p);
        undo = { p, seq, base: seen.base, inc: seen.baseInc, f: current[p]?.f === 1 ? 1 : 0 };
      } else saveEvents?.capture(p, seq, await controller.inspect());
    });
  }
  function remove(p) {
    return action(async () => {
      saveEvents?.cancel(p);
      const s = await controller.inspect(), f = optimistic(s)[p]?.f === 1 ? 1 : 0, seq = await controller.action("remove", p);
      undo = { p, seq, base: s.inst.rev, inc: s.inc, f };
    });
  }
  window.PaintlyWishlist = { v2: true, isolated: true, ready: false, snapshot: () => viewSnapshot({ items: state.items, details: state.details, ready: !!state.last, blocked: controller.blocked, readOnly: controller.readOnly }), has: (h) => handles().includes(h), getAll: handles, count: () => ids().length, toggle, remove: (h) => target(h).then(remove), prune: () => {
  }, whenReady: null };
  async function fallback() {
    state.stopped = true;
    clearTimeout(timer);
    controller.close();
    if (controller.phase === "guest") {
      await installGuest({ cfg, controller, device });
      return;
    }
    await installLegacyView({ cfg: { ...cfg, legacySnapshot: controller.phase === "legacy" ? cfg.legacySnapshot : [] }, controller });
  }
  function placeholder(p, value) {
    const li = node("li", null, "pw-item pw-v2-unavailable");
    li.dataset.pid = p;
    li.append(node("h3", tr("saved_design")), node("p", tr(["missing", "unavailable"].includes(value.status) ? "unavailable" : value.status === "not_in_market" ? "market" : "retry")));
    if (!["missing", "unavailable", "not_in_market"].includes(value.status)) li.append(button(tr("try_again"), () => {
      state.rows.delete(p);
      li.remove();
      render();
    }));
    li.append(button(tr("remove"), () => remove(p.startsWith("h:") ? p : Number(p))));
    return li;
  }
  async function render() {
    if (controller.blocked || state.stopped || !lifecycle.active) return;
    const grid = document.querySelector("#pw-wishlist-grid");
    if (!grid) return;
    const all = ids(), view = step2 ? step2.view(all) : { list: all }, wanted = view.list.slice(0, state.limit), want = new Set(wanted);
    const handleTargets = wanted.filter((p) => p.startsWith("h:") && !state.rows.has(p));
    let handleData = /* @__PURE__ */ new Map();
    if (handleTargets.length) {
      try {
        const resolved = await controller.resolve(handleTargets.map((p) => p.slice(2)), "handles");
        for (const r of resolved) {
          const p = "h:" + r.input;
          if (["listed", "unlisted"].includes(r.state)) handleData.set(p, await cards.one(r.id, r.handle));
          else handleData.set(p, { status: r.state });
        }
      } catch {
      }
    }
    const nums = wanted.filter((p) => !p.startsWith("h:") && !state.rows.has(p)).map(Number);
    let data;
    try {
      data = await cards.batch(nums);
    } catch {
      data = /* @__PURE__ */ new Map();
    }
    if (controller.blocked || state.stopped || !lifecycle.active) return;
    const keep = holdFocus(document, grid);
    for (const [p, li] of state.rows) if (!want.has(p)) {
      li.remove();
      state.rows.delete(p);
    }
    let at = grid.firstElementChild;
    for (const p of wanted) {
      if (!state.items[p]) continue;
      let li = state.rows.get(p);
      if (!li) {
        const value = data.get(p) || handleData.get(p) || { status: "unknown" };
        state.details.set(p, value);
        li = value.node || placeholder(p, value);
        if (value.meta) {
          state.mapped.set(p, value.meta.handle);
          state.metas.set(p, value.meta);
        }
        state.rows.set(p, li);
      }
      step2?.decorate(li, p);
      if (li !== at) grid.insertBefore(li, at);
      else at = at.nextElementSibling;
    }
    keep();
    grid.hidden = !ids().length;
    const count = document.querySelector("#pw-count");
    if (count) {
      count.textContent = ids().length;
      count.hidden = !ids().length;
    }
    const empty = document.querySelector("#pw-wishlist-empty");
    if (empty) empty.hidden = !!ids().length;
    const loading = document.querySelector("#pw-wishlist-loading");
    if (loading) loading.hidden = true;
    document.querySelector(".pw-sechead")?.toggleAttribute("hidden", !ids().length);
    let more = document.getElementById("pw-v2-more");
    if (!more) {
      more = button(tr("more"), () => {
        state.limit += 24;
        render();
      });
      more.id = "pw-v2-more";
      grid.after(more);
    }
    more.hidden = view.list.length <= state.limit;
    step2?.after(all, view.list);
    sync();
  }
  function repaint() {
    state.paint = state.paint.then(render).catch(() => status(tr("retry")));
    return state.paint;
  }
  async function questions() {
    const host = document.getElementById("pw-v2-attention");
    if (!host || !state.last) return;
    const rows = state.last.rows.slice(0, questionLimit), missing = rows.map((r) => r.p).filter((p) => Number.isSafeInteger(p) && !state.metas.has(String(p)));
    try {
      for (const [p, v] of await cards.batch(missing)) {
        if (v.meta) state.metas.set(String(p), v.meta);
      }
    } catch {
    }
    if (controller.blocked) return;
    host.replaceChildren();
    if (!state.last.rows.length) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    host.append(node("h2", tr("waiting")));
    const desc = (row) => row.intent?.presence === false ? "confirm_remove" : row.intent?.nextUp !== null && row.intent?.nextUp !== void 0 ? "confirm_next" : "confirm_save";
    for (const row of rows) {
      const box = node("div", null, "pw-v2-question"), title = state.metas.get(String(row.p))?.title;
      const shown = confirmations.get(row.id);
      if (shown && shown.row.version !== row.version) confirmations.delete(row.id);
      box.append(node("p", (title ? title + " \u2014 " : "") + tr(desc(row))));
      if (confirmations.has(row.id)) {
        for (const [label, yes] of [["yes", true], ["no", false]]) box.append(button(tr(label), () => action(async () => {
          const capture = confirmations.get(row.id);
          confirmations.delete(row.id);
          if (!capture) return;
          const result = await controller.outbox.confirm(row.id, yes, capture.token);
          if (["needs_reconfirm", "stale_confirmation", "stale_transition"].includes(result)) state.notice = tr("changed");
        })));
      } else box.append(button(tr("review"), async () => {
        try {
          const shown2 = await controller.outbox.display(row.id);
          if (shown2) confirmations.set(row.id, shown2);
          await refresh();
        } catch {
          state.notice = tr("retry");
          status(state.notice);
        }
      }));
      host.append(box);
    }
    if (state.last.rows.length > questionLimit) host.append(button(tr("more_changes"), () => {
      questionLimit += 24;
      questions();
    }));
  }
  async function visibleHearts() {
    if (!lifecycle.active) return;
    const handles2 = [...new Set([...document.querySelectorAll("[data-wishlist-btn]")].map((b) => b.dataset.productHandle).filter((h) => h && !seenHearts.has(h)))];
    for (const h of handles2) seenHearts.add(h);
    if (!handles2.length) return;
    try {
      const rows = await controller.resolve(handles2, "handles");
      if (controller.blocked || state.stopped || !lifecycle.active) return;
      for (const r of rows) if (Number.isSafeInteger(r.id) && r.id > 0 && r.handle) state.mapped.set(String(r.id), r.handle);
      sync();
    } catch {
    }
  }
  function publish() {
    document.dispatchEvent(new CustomEvent("wishlist:changed", { detail: { list: handles(), v2: true, count: ids().length } }));
  }
  function redact() {
    step2?.reset();
    confirmations.clear();
    marketing?.reset();
    saveEvents?.reset();
    clearTimeout(marketingTimer);
    state.items = {};
    state.last = null;
    state.mapped.clear();
    state.metas.clear();
    state.details.clear();
    state.rows.clear();
    document.getElementById("pw-v2-notices")?.remove();
    document.querySelector("#pw-wishlist-grid")?.replaceChildren();
    document.querySelector("#pw-v2-attention")?.replaceChildren();
    document.querySelector("#pw-v2-imports")?.replaceChildren();
    document.querySelector("#pw-v2-undo")?.remove();
    window.dispatchEvent(new CustomEvent("paintly:wishlist-scope-changed"));
    document.querySelector("#pw-v2-more")?.remove();
    const count = document.querySelector("#pw-count");
    if (count) count.textContent = "0";
    sync();
    status(tr("session"));
    publish();
  }
  async function accountDetails() {
    if (!document.getElementById("pa-saved")) return;
    const wanted = ids().slice(0, 6), missing = wanted.filter((p) => !p.startsWith("h:") && !state.details.has(p));
    if (!missing.length) return;
    let data;
    try {
      data = await cards.batch(missing.map(Number));
    } catch {
      data = /* @__PURE__ */ new Map();
    }
    if (controller.blocked || state.stopped || !lifecycle.active) return;
    for (const p of missing) if (state.items[p]) {
      const value = data.get(p) || { status: "unknown" };
      state.details.set(p, value);
      if (value.meta) {
        state.mapped.set(p, value.meta.handle);
        state.metas.set(p, value.meta);
      }
    }
  }
  async function refresh() {
    if (state.stopped || !lifecycle.active) return;
    if (controller.blocked) {
      redact();
      return;
    }
    const s = await controller.inspect();
    if (!s) {
      if (controller.blocked) redact();
      return;
    }
    state.last = s;
    state.items = optimistic(s);
    window.PaintlyWishlist.ready = true;
    const unsent = Object.keys(s.outbox).length;
    status(controller.warning || controller.outbox.warning || state.notice || (controller.readOnly ? tr("paused") : unsent ? tr("saving") : s.rows.length ? tr("waiting") : tr("saved")));
    sync();
    publish();
    state.paint = state.paint.then(visibleHearts).then(accountDetails).then(render).then(questions).then(noticesView).then(importView).then(undoView).catch(() => status(tr("retry")));
    await state.paint;
    if (controller.blocked) {
      redact();
      return;
    }
    publish();
    clearTimeout(marketingTimer);
    if (marketing) marketingTimer = setTimeout(() => marketing.run(), 700);
  }
  document.addEventListener("click", (e) => {
    if (state.stopped) return;
    const b = e.target.closest("[data-wishlist-btn]");
    if (!b) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!b.dataset.productHandle) return;
    toggle(b.dataset.productHandle);
  }, true);
  window.addEventListener("beforeunload", (e) => {
    if (state.last && Object.keys(state.last.outbox).length) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
  window.addEventListener("pagehide", () => {
    lifecycle.hide();
    clearTimeout(timer);
    clearTimeout(refreshTimer);
    refreshTimer = null;
    clearTimeout(marketingTimer);
  });
  window.addEventListener("pageshow", async (e) => {
    lifecycle.show();
    if (!controller.outbox || controller.blocked) return;
    if (e.persisted) {
      try {
        const who = await controller.whoami();
        if (who.guest || who.cid !== controller.scope.cid || who.sid !== controller.scope.sid) {
          await controller.suspend();
          return;
        }
        await controller.outbox.getState();
        seenHearts.clear();
        await refresh();
      } catch {
        status(tr("retry"));
        return;
      }
    }
    send();
  });
  window.addEventListener("online", () => send());
  window.addEventListener("focus", async () => {
    if (!controller.outbox || !lifecycle.active) return;
    try {
      const who = await controller.whoami();
      if (who.guest || who.cid !== controller.scope.cid || who.sid !== controller.scope.sid) {
        await controller.suspend();
        return;
      }
      if (!controller.blocked) {
        await controller.outbox.getState();
        await refresh();
        send();
      }
    } catch {
      status(tr("retry"));
    }
  });
  async function init() {
    try {
      device = await DeviceStore.open();
      let raw = [];
      try {
        raw = JSON.parse(localStorage.getItem("paintly_wishlist") || "[]");
      } catch {
      }
      await device.capture(raw);
      const phase = await controller.start();
      if (phase === "legacy") {
        try {
          localStorage.setItem("pk_wl2:kl:bound", "1");
        } catch {
        }
      }
      if (["guest", "legacy"].includes(phase)) {
        await fallback();
        return;
      }
      if (phase !== "account") {
        status(tr("session"));
        return;
      }
      imports = new Imports(controller);
      step2 = createAccountStep2({ cfg, state, controller, cards, action, repaint, ids, onRemoved: (p) => saveEvents?.cancel(p) });
      marketing = new BranchBSync({ controller, enabled: cfg.branchB === true, market: { country: cfg.country, currency: cfg.currency, locale: cfg.locale }, identity: async () => {
        if (!cfg.email || controller.blocked) return null;
        const who = await controller.whoami();
        return !who.guest && who.cid === controller.scope.cid ? { customer: who.cid, key: who.sid + "|" + cfg.email } : null;
      }, loadFresh: async (ids2) => {
        const fresh = new Cards({ controller, root: cfg.root || "/", country: cfg.country || "US" });
        return [...(await fresh.batch(ids2.map(Number))).values()].map((x) => x.meta).filter(Boolean).map((x) => ({ ...x, compareAt: x.compare || 0, url: new URL(x.url, location.origin).href }));
      }, sink: (payload) => {
        return marketingSDK.call("identify", { email: cfg.email, ...payload });
      }, afterPrepared: (p) => saveEvents?.run(p) });
      saveEvents = new ConfirmedSaveEvents({ controller, identity: marketing.identity, enabled: cfg.branchB === true, sink: (name, payload) => {
        return marketingSDK.call("track", name, payload);
      } });
      try {
        localStorage.setItem("pk_wl2:kl:bound", "1");
      } catch {
      }
      await refresh();
      step2.loadMarks();
      loadImports().catch(() => {
        state.notice = tr("import_retry");
        status(state.notice);
      });
      imports.relink().then((changed) => {
        if (changed) send();
      }).catch(() => {
      });
      send();
    } catch {
      status(tr("retry"));
      sync();
      const host = document.querySelector(".pw-page__head");
      if (host && !host.querySelector("[data-v2-retry]")) {
        const b = button(tr("try_again"), () => location.reload());
        b.dataset.v2Retry = "";
        host.append(b);
      }
    }
  }
  new MutationObserver(() => {
    if (!state.stopped && !controller.blocked && controller.phase === "account" && [...document.querySelectorAll("[data-wishlist-btn]")].some((b) => b.dataset.productHandle && !seenHearts.has(b.dataset.productHandle))) scheduleRefresh();
  }).observe(document.body, { childList: true, subtree: true });
  window.PaintlyWishlist.whenReady = init();
  async function loadImports() {
    if (importsLoaded || !imports || controller.blocked) return;
    importsLoaded = true;
    try {
      const local = await device.read(), sources = local.sets.map((x) => ({ kind: "device", handles: x.handles }));
      const guest = Object.keys(local.guest).filter((p) => p.startsWith("h:") && !local.copied.includes(p.slice(2))).map((p) => p.slice(2));
      if (guest.length) sources.push({ kind: "device", handles: guest, next: guest.filter((h) => local.guest["h:" + h]?.f === 1) });
      const r = controller.recovery;
      if (r?.pend?.length || r?.um?.length) sources.push({ kind: "recovery", handles: [...r.pend, ...r.um].map((x) => x[0]), rv: r.rv });
      for (const source of sources) {
        const offer = await imports.offer(source);
        if (offer && (offer.ids.length || offer.unresolved.length || offer.next?.length)) offers.push(offer);
      }
      await importView();
    } catch (e) {
      importsLoaded = false;
      throw e;
    }
  }
  async function importView() {
    const host = document.getElementById("pw-v2-imports");
    if (!host || !state.last || controller.blocked) return;
    host.replaceChildren();
    offers = offers.filter((o) => !state.last.jobs.some((j) => j.id === o.id));
    for (const offer of offers) {
      const box = node("div", null, "pw-v2-question");
      const picks = offer.next?.length || 0;
      box.append(node("p", offer.ids.length || !picks ? tr(offer.kind === "device" ? offer.ids.length === 1 ? "import_device_one" : "import_device_many" : offer.ids.length === 1 ? "import_ask_one" : "import_ask").replace("{count}", offer.ids.length) : tr(picks === 1 ? "import_next_only_one" : "import_next_only").replace("{count}", picks)));
      if (picks && offer.ids.length) box.append(node("p", tr(picks === 1 ? "import_next_one" : "import_next").replace("{count}", picks)));
      if (offer.unresolved.length) box.append(node("p", tr("import_missing").replace("{count}", offer.unresolved.length)));
      for (const [label, yes] of [["yes", true], ["no", false]]) box.append(button(tr(label), () => action(async () => {
        const result = await imports.answer(offer, yes);
        if (result === "changed") {
          offers = offers.filter((x) => x !== offer);
          importsLoaded = false;
          await loadImports();
          state.notice = tr("changed");
        } else offers = offers.filter((x) => x !== offer);
      })));
      host.append(box);
    }
    for (const job of state.last.jobs) {
      if (job.dismissed || ["done", "declined", "running", "finishing", "reviewed"].includes(job.status)) continue;
      const box = node("div", null, "pw-v2-question");
      box.append(node("p", tr(job.status === "full" ? "import_full" : job.status === "unresolved" ? "import_missing" : "import_review").replace("{count}", job.unresolved?.length || (job.failed?.length || 0) + (job.todo?.length || 0))));
      if (["full", "review"].includes(job.status)) box.append(button(tr("review"), () => action(() => imports.review(job.id))));
      if (job.unresolved?.length) box.append(button(tr("try_again"), () => action(() => imports.recheck(job.id))));
      box.append(button(tr("dismiss"), () => action(() => imports.dismiss(job.id))));
      host.append(box);
    }
    if (controller.recovery?.ov) host.append(node("p", tr("import_overflow")));
    host.hidden = !host.childNodes.length;
  }
  async function undoView() {
    let host = document.getElementById("pw-v2-undo");
    const label = undoState(state.last, undo);
    if (!label) {
      host?.remove();
      return;
    }
    if (!host) {
      host = node("div", null, "pw-v2-question");
      host.id = "pw-v2-undo";
      document.querySelector(".pw-page__head")?.append(host);
    }
    host.replaceChildren(node("span", tr(label)), button(tr("undo"), () => action(async () => {
      const u = undo;
      undo = null;
      const s = await controller.inspect();
      if (!s || s.inc !== u.inc) return;
      const pending2 = s.outbox[u.seq], result = s.known[u.seq], r = result?.w?.[0] === "r" ? result.w[1] : void 0;
      if (!pending2 && r === void 0) {
        state.notice = tr("changed");
        return;
      }
      await controller.action("save", u.p, { base: u.base, baseInc: u.inc, ...r === void 0 ? {} : { r } });
      if (u.f === 1 && !String(u.p).startsWith("h:")) await controller.action("next", u.p, { on: true, base: u.base, baseInc: u.inc });
    })));
  }
  async function noticesView() {
    let host = document.getElementById("pw-v2-notices");
    const notices = visibleNotices(state.last || {});
    if (!notices.length || controller.blocked) {
      host?.remove();
      return;
    }
    if (!host) {
      host = node("section");
      host.id = "pw-v2-notices";
      host.setAttribute("aria-live", "polite");
      document.querySelector("#pw-v2-attention, .pw-page__head")?.after(host);
    }
    host.replaceChildren();
    for (const notice of notices.slice(0, noticeLimit)) {
      const p = notice.op?.p ?? notice.p, title = state.metas.get(String(p))?.title || tr("saved_design"), key2 = noticeKey(notice), box = node("div", null, "pw-v2-question");
      box.append(node("p", title + " \u2014 " + tr(noticeText(notice))), button(tr("dismiss"), () => action(() => controller.outbox.tx((s) => {
        s.notices = s.notices.filter((n) => noticeKey(n) !== key2);
      }))));
      host.append(box);
    }
    if (notices.length > noticeLimit) host.append(button(tr("more_changes"), () => {
      noticeLimit += 24;
      noticesView();
    }));
  }
})();
